// Insights Module - unified Map + Data view for Locations and Assets
// VERSION: 2026-05-15 unified locations + assets
console.log('🔄 Loading insights.js - unified locations+assets');

import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { t } from '../core/i18n.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { createCalendarPicker } from '../utils/calendar-picker.js';
import { renderSyncedEnvCharts, destroyEnvCharts } from './env-charts.js';
import { exportEnvSensorToExcel, generateEnvSensorPDF } from './env-export.js';

// ===========================================================================
// Entity Config
// ---------------------------------------------------------------------------
// The same view backs two menu entries (My Locations / My Assets). The
// config below abstracts the differences. Internal variable/function names
// still use "location" wording for historical reasons; treat it as the
// generic "entity" of whichever entityType is active.
// ===========================================================================
export const ENTITY_CONFIGS = {
    location: {
        type: 'location',
        navId: 'my-locations',
        listPath: '/locations',           // appended to API_BASE_URL
        responseKey: 'locations',         // key in {locations, total} response
        singleByIdPath: (id) => `/locations/${id}`,
        labelSingular: 'Location',
        labelPlural: 'Locations',
        labelLoadingPlural: 'Loading locations...',
        // Map marker coordinates for an entity
        extractPosition(entity) {
            if (entity && entity.latitude != null && entity.longitude != null) {
                return {
                    lat: entity.latitude,
                    lng: entity.longitude,
                    source: 'home_location',
                    timestamp: null
                };
            }
            return null;
        },
        // The "address" line shown under the entity name
        extractAddress(entity) {
            return entity && entity.address ? entity.address : '';
        }
    },
    asset: {
        type: 'asset',
        navId: 'my-assets',
        listPath: '/assets',
        responseKey: 'assets',
        singleByIdPath: (id) => `/assets/${id}`,
        labelSingular: 'Asset',
        labelPlural: 'Assets',
        labelLoadingPlural: 'Loading assets...',
        extractPosition(entity) {
            if (!entity) return null;
            // Prefer live GPS position
            const lp = entity.last_position;
            if (lp && lp.latitude != null && lp.longitude != null) {
                return {
                    lat: lp.latitude,
                    lng: lp.longitude,
                    source: lp.source || 'gps',
                    timestamp: lp.timestamp || null
                };
            }
            // Fall back to home location enrichment on the asset
            if (entity.home_location_latitude != null && entity.home_location_longitude != null) {
                return {
                    lat: entity.home_location_latitude,
                    lng: entity.home_location_longitude,
                    source: 'home_location',
                    timestamp: null
                };
            }
            return null;
        },
        extractAddress(entity) {
            return entity && entity.home_location_address ? entity.home_location_address : '';
        }
    }
};

// Active entity config (set by show* entry points). Default to 'location' for safety.
let activeEntityConfig = ENTITY_CONFIGS.location;
function getEntityConfig() { return activeEntityConfig; }

// Module state
let myLocationsMap = null;
let myLocationsMarkers = [];
let userLocationMarker = null;
let googleGeocoder = null;
let mapMoveTimeout = null;
let lastLoadedBounds = null;
let isUserInteracting = false;

/**
 * Get current user from localStorage
 */
function getCurrentUser() {
    try {
        const userStr = localStorage.getItem('current_user');
        return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
        console.error('Error getting current user:', error);
        return null;
    }
}

/**
 * Get current user from API
 */
async function getCurrentUserFromAPI() {
    return await apiCall('/auth/me', {
        headers: { 'Authorization': `Bearer ${auth.getToken()}` }
    });
}

/**
 * Format date time string
 */
function formatDateTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Render the "Latest Log" table cell for a location.
 * Handles card-reader check-in/out events and environmental sensor readings.
 */
function renderLatestLogCell(log) {
    if (!log) {
        return `<span class="text-gray-400 text-sm">${t('locations.noActivity')}</span>`;
    }
    const timeStr = log.timestamp ? formatDateTime(log.timestamp) : '';
    const timePart = `<span class="text-sm font-medium text-gray-900 inline-block w-32 flex-shrink-0 tabular-nums">${timeStr}</span>`;

    if (log.event_type === 'environmental') {
        const parts = [];
        if (log.temperature !== null && log.temperature !== undefined) {
            parts.push(`${Number(log.temperature).toFixed(1)}°C`);
        }
        if (log.humidity !== null && log.humidity !== undefined) {
            parts.push(`${Number(log.humidity).toFixed(0)}%`);
        }
        const readingStr = parts.join(' · ') || t('locations.environmentalReading') || 'Reading';
        return `
            <div class="flex items-center text-sm whitespace-nowrap">
                ${timePart}
                <span class="font-medium text-amber-600">${readingStr}</span>
            </div>
        `;
    }

    const eventType = log.event_type === 'check_in' ? t('locations.checkIn') : t('locations.checkOut');
    const userName = log.user ? log.user.name : 'Unknown User';
    const eventClass = log.event_type === 'check_in' ? 'text-green-600' : 'text-blue-600';
    return `
        <div class="flex items-center text-sm whitespace-nowrap">
            ${timePart}
            <span class="font-medium ${eventClass}">${eventType}: ${userName}</span>
        </div>
    `;
}

/**
 * Display My Locations page with Google Maps
 */
export async function showMyLocations() {
    return _showInsights('location');
}

/**
 * Display My Assets page (same view, asset entity config)
 */
export async function showMyAssets() {
    return _showInsights('asset');
}

async function _showInsights(entityType) {
    activeEntityConfig = ENTITY_CONFIGS[entityType] || ENTITY_CONFIGS.location;
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError(`Please login to view ${activeEntityConfig.labelPlural.toLowerCase()}`);
        return;
    }

    // Refresh user data to get latest work_address
    try {
        const freshUser = await getCurrentUserFromAPI();
        localStorage.setItem('current_user', JSON.stringify(freshUser));
    } catch (error) {
        console.log('⚠️ Could not refresh user data:', error);
    }

    // Reset state so switching menus does not show stale data
    locationInsightsState.locations = [];
    locationInsightsState.filteredLocations = [];
    locationInsightsState.selectedLocation = null;
    locationInsightsState.expandedLocationId = null;
    locationInsightsState.searchTerm = '';
    locationInsightsState.totalCount = 0;

    updateActiveNavigation(activeEntityConfig.navId);

    // Initialize the view with tabs
    await initMyLocations();
}

/**
 * Initialize My Locations view with tabs
 */
async function initMyLocations() {
    console.log('🏗️ Initializing My Locations view...');
    
    // Check if user is logged in
    if (!auth.isAuthenticated()) {
        console.log('❌ User not logged in, aborting My Locations initialization');
        return;
    }
    
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
        console.error('❌ main-content element not found');
        return;
    }

    // Update page title with tabs
    const pageTitleElement = document.getElementById('page-title');
    if (pageTitleElement) {
        pageTitleElement.innerHTML = `
            <div class="flex items-center space-x-8">
                <button onclick="window.showMyLocationsTab('data')" 
                        id="my-locations-data-tab"
                        class="text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold">
                    Data View
                </button>
                <button onclick="window.showMyLocationsTab('map')" 
                        id="my-locations-map-tab"
                        class="text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium">
                    Map View
                </button>
            </div>
        `;
    }

    mainContent.innerHTML = `
        <!-- Tab Content Container -->
        <div id="my-locations-tab-content" class="h-full">
            <!-- Content will be loaded here -->
        </div>
    `;

    // Show the data tab by default
    await showMyLocationsTab('data');
}

/**
 * Show specific My Locations tab
 */
export async function showMyLocationsTab(tabName) {
    const tabContent = document.getElementById('my-locations-tab-content');
    if (!tabContent) return;

    // Update tab styling
    const mapTab = document.getElementById('my-locations-map-tab');
    const dataTab = document.getElementById('my-locations-data-tab');
    
    if (mapTab && dataTab) {
        if (tabName === 'map') {
            mapTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
            dataTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
        } else {
            mapTab.className = 'text-gray-500 hover:text-gray-700 pb-1 text-xl font-medium';
            dataTab.className = 'text-primary-600 border-b-2 border-primary-500 pb-1 text-xl font-semibold';
        }
    }

    if (tabName === 'map') {
        await renderMyLocationsMapTab();
    } else if (tabName === 'data') {
        await renderMyLocationsDataTab();
    }
}

/** Alias used by My Assets nav so old wiring keeps working. */
export async function showAssetInsightsTab(tabName) {
    // Map old asset-insights tab names to unified names
    if (tabName === 'activity') tabName = 'data';
    if (tabName === 'tracking') tabName = 'map';
    return showMyLocationsTab(tabName);
}

/**
 * Render Map View tab (original content)
 */
async function renderMyLocationsMapTab() {
    const tabContent = document.getElementById('my-locations-tab-content');
    
    tabContent.innerHTML = `
        <div class="flex gap-4 h-[calc(100vh-180px)]">
            <!-- Locations List - Left Side (1/3 width) -->
            <div class="w-1/3 bg-white rounded-lg shadow flex flex-col">
                <div class="px-4 py-5 border-b border-gray-200 flex-shrink-0">
                    <div class="flex items-center justify-end">
                        <div id="map-loading-indicator" class="hidden">
                            <div class="inline-flex items-center text-sm text-gray-600">
                                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading...
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-1 overflow-y-auto overflow-x-auto px-4 py-3">
                    <div id="locations-list" class="space-y-3 min-w-max">
                        <div class="text-center py-8">
                            <svg class="mx-auto h-12 w-12 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p class="mt-2 text-sm text-gray-500">Loading locations...</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Map Container - Right Side (2/3 width) -->
            <div class="w-2/3 bg-white rounded-lg shadow overflow-hidden">
                <div id="locations-map" class="w-full h-full"></div>
            </div>
        </div>
    `;

    await initializeMyLocationsMap();
}

/**
 * Render Data View tab (environmental sensor data)
 */
async function renderMyLocationsDataTab() {
    console.log('📊 Rendering Data View tab...');
    const tabContent = document.getElementById('my-locations-tab-content');
    console.log('Tab content element:', tabContent);

    const cfg = getEntityConfig();
    const labelSingularLower = cfg.labelSingular.toLowerCase();
    const labelPluralLower = cfg.labelPlural.toLowerCase();

    tabContent.innerHTML = `
        <!-- Filter Row - Sticky (aligned with Time Tracking) -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <div class="flex flex-wrap items-end gap-4">
                    <!-- Search by name -->
                    <div class="flex-1 min-w-[200px]">
                        <label class="block text-sm font-medium text-gray-700 mb-1">${t('locations.search') || 'Search'}</label>
                        <input type="text" id="insights-search-input"
                               placeholder="Search ${labelSingularLower} name..."
                               oninput="window.onInsightsSearchInput(this.value)"
                               class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm">
                    </div>

                    <!-- Filter mode radios -->
                    <div class="flex-shrink-0">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Filter mode</label>
                        <div class="flex items-center gap-4 text-sm h-[38px]">
                            <label class="inline-flex items-center cursor-pointer">
                                <input type="radio" name="insights-filter-mode" value="accountLocation"
                                       checked
                                       onchange="window.setInsightsFilterMode('accountLocation')"
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                                <span class="ml-2 text-gray-700">Account / ${cfg.labelSingular}</span>
                            </label>
                            <label class="inline-flex items-center cursor-pointer">
                                <input type="radio" name="insights-filter-mode" value="mapBounds"
                                       onchange="window.setInsightsFilterMode('mapBounds')"
                                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                                <span class="ml-2 text-gray-700">Current map view</span>
                            </label>
                        </div>
                    </div>

                    <!-- Account / Location dropdowns (visible when filterMode === 'accountLocation') -->
                    <div id="insights-account-location-filters" class="flex flex-wrap items-end gap-4">
                        <div class="flex-shrink-0 w-56">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Account</label>
                            <div class="relative">
                                <button type="button" id="insights-account-toggle"
                                        onclick="window.toggleInsightsAccountTree()"
                                        class="block w-full text-left rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm text-sm">
                                    <span id="insights-account-selected">All accounts</span>
                                    <svg class="absolute right-2 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>
                                <div id="insights-account-tree-container" class="hidden absolute z-50 mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg">
                                    <div id="insights-account-tree" class="p-2 max-h-64 overflow-y-auto"></div>
                                </div>
                            </div>
                        </div>

                        <div class="flex-shrink-0 w-56">
                            <label class="block text-sm font-medium text-gray-700 mb-1">${cfg.labelSingular}</label>
                            <div class="relative">
                                <button type="button" id="insights-location-toggle"
                                        onclick="window.toggleInsightsLocationDropdown()"
                                        class="block w-full text-left rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm text-sm">
                                    <span id="insights-location-selected">All ${labelPluralLower}</span>
                                    <svg class="absolute right-2 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>
                                <div id="insights-location-dropdown" class="hidden absolute z-50 mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg">
                                    <div class="p-2 border-b border-gray-200">
                                        <input type="text" id="insights-location-dropdown-search"
                                               placeholder="Search ${labelPluralLower}..."
                                               oninput="window.onInsightsLocationDropdownSearch(this.value)"
                                               class="block w-full px-2 py-1 rounded-md border-gray-300 shadow-sm text-sm">
                                    </div>
                                    <div id="insights-location-dropdown-list" class="max-h-60 overflow-y-auto p-2"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Map-bounds info (visible when filterMode === 'mapBounds') -->
                    <div id="insights-map-bounds-info" class="hidden self-end mb-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5">
                        <span id="insights-map-bounds-text">Showing ${labelPluralLower} within the current Map View bounds.</span>
                    </div>

                    <!-- Loading indicator -->
                    <div id="data-loading-indicator" class="hidden self-end mb-2">
                        <div class="inline-flex items-center text-sm text-gray-600">
                            <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- List - full width -->
        <div class="bg-white shadow rounded-lg mt-3">
            <div id="locations-list-insights">
                <div class="text-center py-8">
                    <svg class="mx-auto h-12 w-12 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p class="mt-2 text-sm text-gray-500">Loading ${labelPluralLower}...</p>
                </div>
            </div>
        </div>

        <!-- Slide-over drawer: sensor data / graphs -->
        <div id="insights-drawer-overlay" class="hidden fixed inset-0 bg-black bg-opacity-30 z-40" onclick="window.closeInsightsDrawer()"></div>
        <div id="insights-drawer"
             class="fixed top-0 right-0 h-full w-full max-w-3xl bg-white shadow-xl z-50 transform translate-x-full transition-transform duration-300 ease-in-out flex flex-col">
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 id="insights-drawer-title" class="text-lg font-semibold text-gray-900 truncate pr-4">Sensor data</h2>
                <button type="button" onclick="window.closeInsightsDrawer()"
                        class="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div id="location-data-chart-container" class="flex-1 overflow-y-auto p-6">
                <div class="text-center py-16 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p class="mt-4 text-sm">Select a ${labelSingularLower} to view sensor data</p>
                </div>
            </div>
        </div>
    `;
    
    console.log('Tab content set, calling loadLocationsListForInsights...');
    // Kick off the accounts tree fetch in parallel with the locations fetch so
    // the Account dropdown is populated as soon as possible (and not blocked
    // by the heavier /locations request).
    loadAccountsForInsights().catch(err => console.warn('Accounts load failed', err));
    // Load locations list
    await loadLocationsListForInsights();

    // Click-outside handler for filter dropdowns (idempotent)
    if (!window.__insightsClickOutsideBound) {
        window.__insightsClickOutsideBound = true;
        document.addEventListener('click', (e) => {
            const acctC = document.getElementById('insights-account-tree-container');
            const acctT = document.getElementById('insights-account-toggle');
            if (acctC && acctT && !acctC.contains(e.target) && !acctT.contains(e.target)) {
                acctC.classList.add('hidden');
            }
            const locC = document.getElementById('insights-location-dropdown');
            const locT = document.getElementById('insights-location-toggle');
            if (locC && locT && !locC.contains(e.target) && !locT.contains(e.target)) {
                locC.classList.add('hidden');
            }
        });
    }
}

// State for location insights
const locationInsightsState = {
    locations: [],
    filteredLocations: [],
    selectedLocation: null,
    searchTerm: '',
    chartInstance: null,
    expandedLocationId: null,  // Track which location has expanded sensor list
    sortColumn: 'name',        // 'name' | 'latestLog' — default is name since
                               // server returns alphabetical pages.
    sortDirection: 'asc',      // 'asc' | 'desc'
    // Filter state for Data View
    filterMode: 'accountLocation', // 'accountLocation' | 'mapBounds'
    selectedAccountIds: new Set(),
    selectedLocationIds: new Set(),
    accountHierarchyMap: {},   // accountId -> { id, name, children: [childIds] }
    accountsTree: [],          // raw tree from API
    locationDropdownSearch: '',
    // Pagination
    pageSize: 300,
    totalCount: 0,
    isLoadingMore: false
};

// Last-known map bounds captured from the Map View (used by 'mapBounds' filter mode)
let myLocationsLastMapBounds = null;

/**
 * Apply current sort to filteredLocations in place
 */
function applyInsightsSort() {
    const { sortColumn, sortDirection } = locationInsightsState;
    const dir = sortDirection === 'asc' ? 1 : -1;
    locationInsightsState.filteredLocations.sort((a, b) => {
        if (sortColumn === 'name') {
            const an = (a.name || '').toLowerCase();
            const bn = (b.name || '').toLowerCase();
            if (an < bn) return -1 * dir;
            if (an > bn) return 1 * dir;
            return 0;
        }
        // latestLog: treat missing logs as the oldest possible timestamp so
        // they sort to the top when ascending (oldest first) and to the
        // bottom when descending (newest first).
        const at = a.latest_log && a.latest_log.timestamp ? a.latest_log.timestamp : '';
        const bt = b.latest_log && b.latest_log.timestamp ? b.latest_log.timestamp : '';
        if (at < bt) return -1 * dir;
        if (at > bt) return 1 * dir;
        return 0;
    });
}

window.sortInsightLocations = function(column) {
    if (locationInsightsState.sortColumn === column) {
        locationInsightsState.sortDirection =
            locationInsightsState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
        locationInsightsState.sortColumn = column;
        // Sensible defaults: name asc, latestLog desc (newest first)
        locationInsightsState.sortDirection = column === 'name' ? 'asc' : 'desc';
    }
    applyInsightsSort();
    renderLocationsListForInsights();
};

window.insightsLoadMore = function() {
    if (locationInsightsState.isLoadingMore) return;
    loadLocationsListForInsights({ append: true });
};

window.insightsLoadAll = function() {
    if (locationInsightsState.isLoadingMore) return;
    loadLocationsListForInsights({ append: true, loadAll: true });
};

/* ---------- Data View filter (search / account+location / map bounds) ---------- */

function applyInsightsFilter() {
    const state = locationInsightsState;
    let list = state.locations.slice();

    // All filtering (account + search + map bounds) is now applied server-side
    // in loadLocationsListForInsights(). The only client-side narrowing left
    // is the explicit "selected location IDs" set the user may pick from the
    // location dropdown.
    if (state.selectedLocationIds.size > 0) {
        list = list.filter(loc => state.selectedLocationIds.has(loc.id));
    }

    state.filteredLocations = list;
    applyInsightsSort();
}

window.onInsightsSearchInput = function(value) {
    locationInsightsState.searchTerm = value || '';
    // Debounce server reload on text input
    if (window.__insightsSearchDebounce) clearTimeout(window.__insightsSearchDebounce);
    window.__insightsSearchDebounce = setTimeout(() => {
        loadLocationsListForInsights();
    }, 250);
};

window.setInsightsFilterMode = function(mode) {
    locationInsightsState.filterMode = mode;
    const alBlock = document.getElementById('insights-account-location-filters');
    const mbBlock = document.getElementById('insights-map-bounds-info');
    if (alBlock) alBlock.classList.toggle('hidden', mode !== 'accountLocation');
    if (mbBlock) mbBlock.classList.toggle('hidden', mode !== 'mapBounds');
    if (mode === 'mapBounds') {
        const text = document.getElementById('insights-map-bounds-text');
        if (text && !myLocationsLastMapBounds) {
            text.textContent = 'No map bounds captured yet — open Map View once to set the area.';
        } else if (text) {
            text.textContent = 'Showing locations within the current Map View bounds.';
        }
        // In mapBounds mode we want all locations from server (no account filter)
        loadLocationsListForInsights();
    } else {
        loadLocationsListForInsights();
    }
};

/* ---------- Account tree (Data View filter) ---------- */

function buildAccountHierarchyMapInsights(accounts) {
    const map = {};
    function walk(account) {
        const accountId = account._id || account.id;
        const childIds = [];
        if (account.children && account.children.length > 0) {
            account.children.forEach(child => {
                childIds.push(child._id || child.id);
                walk(child);
            });
        }
        map[accountId] = {
            id: accountId,
            name: account.account_name || account.name,
            children: childIds
        };
    }
    accounts.forEach(walk);
    return map;
}

function getAllAccountIdsIncludingDescendantsInsights(accountId) {
    const result = [accountId];
    const add = (id) => {
        const a = locationInsightsState.accountHierarchyMap[id];
        if (a && a.children.length > 0) {
            a.children.forEach(cid => { result.push(cid); add(cid); });
        }
    };
    add(accountId);
    return result;
}

async function loadAccountsForInsights() {
    try {
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        const accountsTree = data.accounts_tree || data;
        locationInsightsState.accountsTree = accountsTree;
        locationInsightsState.accountHierarchyMap = buildAccountHierarchyMapInsights(accountsTree);
        const treeContainer = document.getElementById('insights-account-tree');
        if (treeContainer) {
            treeContainer.innerHTML = renderInsightsAccountTree(accountsTree, 0);
        }
    } catch (err) {
        console.warn('loadAccountsForInsights error', err);
    }
}

function renderInsightsAccountTree(accounts, level) {
    let html = '';
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 16;
        const expandByDefault = level === 0;
        const checked = locationInsightsState.selectedAccountIds.has(accountId);
        const safeName = accountName.replace(/'/g, "\\'");
        html += `
            <div class="insights-account-tree-item">
                <div class="flex items-center py-1 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="window.toggleInsightsAccountItem('ins-tree-${accountId}')"
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                            <svg id="ins-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform${expandByDefault ? ' rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="w-5 mr-1"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="checkbox" value="${accountId}" ${checked ? 'checked' : ''}
                               onchange="window.selectInsightsAccount('${accountId}', '${safeName}', this.checked)"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="ins-tree-${accountId}"${expandByDefault ? '' : ' class="hidden"'}>
                        ${renderInsightsAccountTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    return html;
}

window.toggleInsightsAccountItem = function(itemId) {
    const item = document.getElementById(itemId);
    const icon = document.getElementById(itemId.replace('ins-tree-', 'ins-tree-icon-'));
    if (item) item.classList.toggle('hidden');
    if (icon) icon.classList.toggle('rotate-90');
};

window.toggleInsightsAccountTree = function() {
    const c = document.getElementById('insights-account-tree-container');
    if (c) c.classList.toggle('hidden');
};

window.selectInsightsAccount = function(accountId, accountName, checked) {
    if (checked) locationInsightsState.selectedAccountIds.add(accountId);
    else locationInsightsState.selectedAccountIds.delete(accountId);
    const display = document.getElementById('insights-account-selected');
    if (display) {
        const n = locationInsightsState.selectedAccountIds.size;
        display.textContent = n === 0 ? 'All accounts'
            : n === 1 ? accountName
            : `${n} accounts selected`;
    }
    // Clear location selections that may no longer be valid under the new account scope
    locationInsightsState.selectedLocationIds.clear();
    const locDisplay = document.getElementById('insights-location-selected');
    if (locDisplay) locDisplay.textContent = 'All locations';
    loadLocationsListForInsights();
};

/* ---------- Location multi-select dropdown ---------- */

window.toggleInsightsLocationDropdown = function() {
    const d = document.getElementById('insights-location-dropdown');
    if (d) {
        d.classList.toggle('hidden');
        renderInsightsLocationDropdownList();
    }
};

window.onInsightsLocationDropdownSearch = function(value) {
    locationInsightsState.locationDropdownSearch = value || '';
    renderInsightsLocationDropdownList();
};

function renderInsightsLocationDropdownList() {
    const container = document.getElementById('insights-location-dropdown-list');
    if (!container) return;
    const q = locationInsightsState.locationDropdownSearch.trim().toLowerCase();
    const list = locationInsightsState.locations
        .filter(loc => !q || (loc.name || '').toLowerCase().includes(q))
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (list.length === 0) {
        container.innerHTML = `<div class="text-xs text-gray-500 px-2 py-2">No locations</div>`;
        return;
    }
    container.innerHTML = list.map(loc => {
        const checked = locationInsightsState.selectedLocationIds.has(loc.id);
        const safeName = (loc.name || '').replace(/'/g, "\\'").replace(/"/g, '&quot;');
        return `
            <label class="flex items-center py-1 px-1 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" value="${loc.id}" ${checked ? 'checked' : ''}
                       onchange="window.selectInsightsLocation('${loc.id}', '${safeName}', this.checked)"
                       class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                <span class="ml-2 text-sm text-gray-700 truncate">${loc.name || 'Unnamed'}</span>
            </label>
        `;
    }).join('');
}

window.selectInsightsLocation = function(locationId, locationName, checked) {
    if (checked) locationInsightsState.selectedLocationIds.add(locationId);
    else locationInsightsState.selectedLocationIds.delete(locationId);
    const display = document.getElementById('insights-location-selected');
    if (display) {
        const n = locationInsightsState.selectedLocationIds.size;
        display.textContent = n === 0 ? 'All locations'
            : n === 1 ? locationName
            : `${n} locations selected`;
    }
    applyInsightsFilter();
    renderLocationsListForInsights();
};

/**
 * Load locations list for insights
 * Account & location filters are applied server-side (mirrors Time Tracking).
 */
async function loadLocationsListForInsights({ append = false, loadAll = false } = {}) {
    console.log('🔍 Loading locations for insights...', { append, loadAll });

    try {
        const state = locationInsightsState;
        // Page boundaries
        const skip = append ? state.locations.length : 0;
        const limit = loadAll ? 5000 : state.pageSize;

        if (append) {
            state.isLoadingMore = true;
            // Update the "Load more" button to show a spinner
            const lm = document.getElementById('insights-load-more-btn');
            if (lm) {
                lm.disabled = true;
                lm.textContent = t('common.loading') || 'Loading...';
            }
        }

        const params = new URLSearchParams();
        // Account filter (server-side, applied in BOTH modes so Data View
        // and Map View are scoped the same way).
        if (locationInsightsState.selectedAccountIds.size > 0) {
            const expanded = new Set();
            locationInsightsState.selectedAccountIds.forEach(id => {
                getAllAccountIdsIncludingDescendantsInsights(id).forEach(x => expanded.add(x));
            });
            expanded.forEach(id => params.append('account_id', id));
            console.log('[INSIGHTS] Account filter expanded:', Array.from(expanded));
        }
        // Search term (server-side)
        const q = (locationInsightsState.searchTerm || '').trim();
        if (q) params.append('search', q);

        // Map-bounds filter mode: send the same bounds the Map View uses, so the
        // server returns the same set of locations. If no bounds are captured
        // yet (user never opened Map View), we just omit them and load all.
        if (locationInsightsState.filterMode === 'mapBounds' && myLocationsLastMapBounds) {
            const { north, south, east, west } = myLocationsLastMapBounds;
            params.append('north', north);
            params.append('south', south);
            params.append('east', east);
            params.append('west', west);
            console.log('[INSIGHTS] Using map bounds:', myLocationsLastMapBounds);
        }

        // Pagination
        params.append('skip', String(skip));
        params.append('limit', String(limit));

        const cfg = getEntityConfig();
        const url = `${API_BASE_URL}${cfg.listPath}${params.toString() ? `?${params.toString()}` : ''}`;
        console.log('[INSIGHTS] Fetching:', url);

        const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('[INSIGHTS] Response error:', errorText);
            throw new Error(`Failed to fetch ${cfg.labelPlural.toLowerCase()}: ${response.status}`);
        }

        const data = await response.json();
        const pageLocations = data[cfg.responseKey] || data.locations || data.assets || [];
        const totalCount = typeof data.total === 'number' ? data.total : pageLocations.length;
        console.log('[INSIGHTS] Got', pageLocations.length, 'of', totalCount, 'locations from server');

        state.totalCount = totalCount;
        state.locations = append ? state.locations.concat(pageLocations) : pageLocations;
        applyInsightsFilter();

        // Kick off accounts hierarchy load (non-blocking, only once)
        if (locationInsightsState.accountsTree.length === 0) {
            loadAccountsForInsights().catch(err => console.warn('Accounts load failed', err));
        }

        // Populate location dropdown list (so user can further narrow by location)
        renderInsightsLocationDropdownList();

        renderLocationsListForInsights();
    } catch (error) {
        console.error('❌ Error loading locations:', error);
        showError('Failed to load locations');
        const container = document.getElementById('locations-list-insights');
        if (container) {
            container.innerHTML = `
                <div class="text-center py-8 text-red-600">
                    <p class="text-sm">Failed to load locations</p>
                    <p class="text-xs mt-2">${error.message}</p>
                </div>
            `;
        }
    } finally {
        locationInsightsState.isLoadingMore = false;
    }
}

/**
 * Render locations list in left column
 */
function renderLocationsListForInsights() {
    console.log('🎨 Rendering locations list...');
    const container = document.getElementById('locations-list-insights');
    console.log('Container element:', container);
    
    if (!container) {
        console.error('❌ Container element not found!');
        return;
    }
    
    console.log('Filtered locations count:', locationInsightsState.filteredLocations.length);
    
    if (locationInsightsState.filteredLocations.length === 0) {
        console.log('No locations to display');
        container.innerHTML = `
            <div class="text-center py-8 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p class="mt-2">${t('locations.noLocationsFound')}</p>
            </div>
        `;
        return;
    }
    
    console.log('Rendering', locationInsightsState.filteredLocations.length, 'locations');
    
    // Debug: log first location to see its structure
    if (locationInsightsState.filteredLocations.length > 0) {
        console.log('First location object:', locationInsightsState.filteredLocations[0]);
        console.log('First location keys:', Object.keys(locationInsightsState.filteredLocations[0]));
    }
    
    const rowsHtml = locationInsightsState.filteredLocations.map(location => {
        const sensorCount = location.sensor_count || 0;
        const hasSensors = sensorCount > 0;
        const isExpanded = locationInsightsState.expandedLocationId === location.id;
        const isSelected = locationInsightsState.selectedLocation?.id === location.id;

        // Latest log cell (matches Map View styling)
        const latestLogHtml = renderLatestLogCell(location.latest_log);

        // Number of sensors cell - clickable badge to expand sensor list
        let sensorsCellHtml;
        if (hasSensors) {
            sensorsCellHtml = `
                <button
                    onclick="window.toggleLocationSensorList('${location.id}'); event.stopPropagation();"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                    <svg class="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="6"/>
                    </svg>
                    ${sensorCount}
                    <svg class="ml-1 h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            `;
        } else {
            sensorsCellHtml = `<span class="text-gray-400 text-sm">-</span>`;
        }

        // Expanded sensor selection row
        let expandedRowHtml = '';
        if (isExpanded && hasSensors && location.sensors) {
            const sensorsHtml = location.sensors.map(sensor => {
                const isPreferred = location.preferred_sensor_id === sensor.id;
                return `
                    <label class="flex items-center px-3 py-2 rounded hover:bg-gray-50 cursor-pointer">
                        <input type="radio"
                               name="sensor-${location.id}"
                               value="${sensor.id}"
                               ${isPreferred ? 'checked' : ''}
                               onchange="window.setPreferredSensor('${location.id}', '${sensor.id}')"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                        <div class="ml-3 flex-1" onclick="window.loadLocationSensorDataDirect('${location.id}', '${sensor.id}'); event.stopPropagation();">
                            <div class="text-sm font-medium text-gray-900">${sensor.name || 'Unnamed'}</div>
                            <div class="text-xs text-gray-500">${sensor.sensor_id} ${sensor.sensor_type ? '• ' + sensor.sensor_type : ''}</div>
                        </div>
                        ${isPreferred ? '<span class="text-xs text-primary-600 font-medium">Preferred</span>' : ''}
                    </label>
                `;
            }).join('');
            expandedRowHtml = `
                <tr class="bg-gray-50">
                    <td colspan="3" class="px-4 py-3">
                        <div class="space-y-2">${sensorsHtml}</div>
                    </td>
                </tr>
            `;
        }

        const rowBgClass = isSelected ? 'bg-primary-50' : '';

        return `
            <tr class="cursor-pointer hover:bg-gray-50 ${rowBgClass}"
                onclick="window.selectLocationForInsights('${location.id}')">
                <td class="px-4 py-4 align-top">
                    <div class="max-w-xl">
                        <div class="text-sm font-medium text-gray-900 truncate" title="${(location.name || 'Unnamed Location').replace(/"/g, '&quot;')}">${location.name || 'Unnamed Location'}</div>
                        ${location.address ? `<div class="text-xs text-gray-500 truncate" title="${location.address.replace(/"/g, '&quot;')}">${location.address}</div>` : ''}
                    </div>
                </td>
                <td class="px-4 py-4 align-top">
                    ${latestLogHtml}
                </td>
                <td class="px-4 py-4 whitespace-nowrap align-top">
                    ${sensorsCellHtml}
                </td>
            </tr>
            ${expandedRowHtml}
        `;
    }).join('');

    const { sortColumn, sortDirection } = locationInsightsState;
    const loaded = locationInsightsState.locations.length;
    const total = locationInsightsState.totalCount || loaded;
    const hasMore = loaded < total;
    const sortArrow = (col) => {
        if (sortColumn !== col) return '<span class="text-gray-300 ml-1">↕</span>';
        return sortDirection === 'asc'
            ? '<span class="text-gray-700 ml-1">▲</span>'
            : '<span class="text-gray-700 ml-1">▼</span>';
    };

    // Latest-log sort is only meaningful when the full result set is loaded,
    // because skip/limit pagination on the server is ordered by name, not by
    // latest-log timestamp. So we disable that header (with a tooltip) until
    // the user clicks "Load all".
    const latestLogDisabled = hasMore;
    const latestLogHeaderClass = latestLogDisabled
        ? 'px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-not-allowed select-none'
        : 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100';
    const latestLogOnClick = latestLogDisabled ? '' : `onclick="window.sortInsightLocations('latestLog')"`;
    const latestLogTitle = latestLogDisabled
        ? 'Load all locations to sort by latest log'
        : '';

    const paginationFooter = `
        <div class="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
            <span>Showing ${loaded} of ${total}</span>
            ${hasMore ? `
                <div class="flex gap-2">
                    <button type="button" id="insights-load-more-btn"
                            onclick="window.insightsLoadMore()"
                            class="px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 text-xs font-medium">
                        Load more (${Math.min(locationInsightsState.pageSize, total - loaded)})
                    </button>
                    <button type="button" id="insights-load-all-btn"
                            onclick="window.insightsLoadAll()"
                            class="px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 text-xs font-medium">
                        Load all
                    </button>
                </div>
            ` : ''}
        </div>
    `;

    container.innerHTML = `
        <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col"
                            class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                            onclick="window.sortInsightLocations('name')">
                            ${t('locations.location')}${sortArrow('name')}
                        </th>
                        <th scope="col"
                            class="${latestLogHeaderClass}"
                            title="${latestLogTitle}"
                            ${latestLogOnClick}>
                            ${t('locations.latestLog')}${latestLogDisabled ? '' : sortArrow('latestLog')}
                        </th>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ${t('locations.sensors')}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    ${rowsHtml}
                </tbody>
            </table>
            ${paginationFooter}
        </div>
    `;
}

/**
 * Filter locations by search term
 */
window.filterInsightLocations = function(searchTerm) {
    locationInsightsState.searchTerm = searchTerm.toLowerCase();
    
    if (!searchTerm) {
        locationInsightsState.filteredLocations = [...locationInsightsState.locations];
    } else {
        locationInsightsState.filteredLocations = locationInsightsState.locations.filter(location => 
            (location.location_name || '').toLowerCase().includes(searchTerm) ||
            (location.address || '').toLowerCase().includes(searchTerm)
        );
    }
    applyInsightsSort();
    
    renderLocationsListForInsights();
};

/**
 * Toggle sensor list expansion for a location
 */
window.toggleLocationSensorList = function(locationId) {
    console.log('🔄 Toggling sensor list for location:', locationId);
    
    // Toggle expansion state
    if (locationInsightsState.expandedLocationId === locationId) {
        locationInsightsState.expandedLocationId = null;
    } else {
        locationInsightsState.expandedLocationId = locationId;
    }
    
    renderLocationsListForInsights();
};

/**
 * Set preferred sensor for a location
 */
window.setPreferredSensor = async function(locationId, sensorId) {
    console.log('⭐ Setting preferred sensor:', sensorId, 'for location:', locationId);
    
    try {
        const cfg = getEntityConfig();
        // Update entity on backend (preferred sensor PATCH)
        const response = await fetch(`${API_BASE_URL}${cfg.singleByIdPath(locationId)}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                preferred_sensor_id: sensorId
            })
        });
        
        if (!response.ok) {
            throw new Error('Failed to update preferred sensor');
        }
        
        // Update local state
        const location = locationInsightsState.locations.find(l => l.id === locationId);
        if (location) {
            location.preferred_sensor_id = sensorId;
        }
        
        // Also update in filteredLocations
        const filteredLocation = locationInsightsState.filteredLocations.find(l => l.id === locationId);
        if (filteredLocation) {
            filteredLocation.preferred_sensor_id = sensorId;
        }
        
        showSuccess('Preferred sensor updated');
        renderLocationsListForInsights();
        
    } catch (error) {
        console.error('Error setting preferred sensor:', error);
        showError('Failed to update preferred sensor');
    }
};

/**
 * Load sensor data directly without showing sensor selection dropdown
 */
window.loadLocationSensorDataDirect = async function(locationId, sensorId) {
    console.log('📊 Loading sensor data directly:', locationId, sensorId);
    await window.loadSelectedSensorData(locationId, sensorId);
};

/**
 * Select a location and load its sensor data
 */
window.selectLocationForInsights = async function(locationId) {
    console.log('🎯 Selecting location:', locationId);
    // Find the location
    const location = locationInsightsState.locations.find(l => l.id === locationId);
    console.log('Found location:', location);
    if (!location) return;
    
    locationInsightsState.selectedLocation = location;
    renderLocationsListForInsights(); // Update selection styling

    // Open the slide-over drawer that hosts the sensor graphs
    window.openInsightsDrawer(location.name || 'Sensor data');

    // First load the sensor data UI
    await loadLocationSensorData(locationId);
    
    // Then auto-load sensor data if location has sensors
    if (location.sensors && location.sensors.length > 0) {
        // Use preferred sensor if set, otherwise use first sensor
        const sensorToLoad = location.preferred_sensor_id 
            ? location.sensors.find(s => s.id === location.preferred_sensor_id)
            : location.sensors[0];
        
        if (sensorToLoad) {
            console.log('📊 Auto-loading sensor:', sensorToLoad.id);
            await window.loadSelectedSensorData(locationId, sensorToLoad.id);
        }
    }
};

/**
 * Open the sensor-data slide-over drawer.
 */
window.openInsightsDrawer = function(title) {
    const drawer = document.getElementById('insights-drawer');
    const overlay = document.getElementById('insights-drawer-overlay');
    const titleEl = document.getElementById('insights-drawer-title');
    if (titleEl) titleEl.textContent = title || 'Sensor data';
    if (overlay) overlay.classList.remove('hidden');
    if (drawer) {
        // Force a reflow so the transition runs when we remove translate-x-full
        void drawer.offsetWidth;
        drawer.classList.remove('translate-x-full');
    }
    // Bind Escape-to-close once
    if (!window.__insightsDrawerEscBound) {
        window.__insightsDrawerEscBound = true;
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') window.closeInsightsDrawer();
        });
    }
};

/**
 * Close the sensor-data slide-over drawer.
 */
window.closeInsightsDrawer = function() {
    const drawer = document.getElementById('insights-drawer');
    const overlay = document.getElementById('insights-drawer-overlay');
    if (drawer) drawer.classList.add('translate-x-full');
    if (overlay) overlay.classList.add('hidden');
    // Clear selection highlight in the list
    locationInsightsState.selectedLocation = null;
    renderLocationsListForInsights();
};

/**
 * Load sensor data for selected location
 */
async function loadLocationSensorData(locationId) {
    console.log('📡 Loading sensor data for location:', locationId);
    const container = document.getElementById('location-data-chart-container');
    if (!container) return;
    
    container.innerHTML = `
        <div class="text-center py-16">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-4 text-sm text-gray-500">Loading sensor data...</p>
        </div>
    `;
    
    try {
        // Get sensor IDs from location object
        const locationSensorIds = (locationInsightsState.selectedLocation?.sensors || []).map(s => s.sensor_id);
        console.log('Location sensor IDs:', locationSensorIds);
        
        if (locationSensorIds.length === 0) {
            container.innerHTML = `
                <div class="text-center py-16 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p class="mt-4 text-sm">No sensors assigned to this location</p>
                </div>
            `;
            return;
        }
        
        // Just create the container for sensor data display
        container.innerHTML = `
            <div class="space-y-6">
                <div>
                    <h3 class="text-lg font-medium text-gray-900 mb-4">${locationInsightsState.selectedLocation.name}</h3>
                </div>
                
                <div id="sensor-data-display">
                    <div class="text-center py-16 text-gray-500">
                        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p class="mt-4 text-sm">Loading sensor data...</p>
                    </div>
                </div>
            </div>
        `;
        
    } catch (error) {
        console.error('Error loading sensor data:', error);
        container.innerHTML = `
            <div class="text-center py-16 text-red-600">
                <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="mt-4 text-sm">Failed to load sensor data</p>
            </div>
        `;
    }
}

/**
 * Load data for selected sensor
 */
window.loadSelectedSensorData = async function(locationId, sensorId) {
    console.log('🔍 loadSelectedSensorData called with locationId:', locationId, 'sensorId:', sensorId);
    
    if (!sensorId) {
        const display = document.getElementById('sensor-data-display');
        if (display) {
            display.innerHTML = `
                <div class="text-center py-16 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p class="mt-4 text-sm">Select a sensor to view its data</p>
                </div>
            `;
        }
        return;
    }

    const display = document.getElementById('sensor-data-display');
    console.log('📍 sensor-data-display element:', display);
    
    if (!display) {
        console.error('❌ sensor-data-display element not found!');
        return;
    }

    display.innerHTML = `
        <div class="text-center py-16">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-4 text-sm text-gray-500">Loading sensor data...</p>
        </div>
    `;

    try {
        console.log('📡 Fetching sensor details from:', `${API_BASE_URL}/sensors/${sensorId}`);
        
        // Fetch sensor details
        const sensorResponse = await fetch(`${API_BASE_URL}/sensors/${sensorId}`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        console.log('📥 Sensor response status:', sensorResponse.status);

        if (!sensorResponse.ok) {
            throw new Error('Failed to fetch sensor details');
        }

        const sensor = await sensorResponse.json();
        console.log('✅ Sensor data received:', sensor);

        // Display data based on sensor type
        if (sensor.sensor_type === 'activity') {
            console.log('📊 Displaying activity sensor data');
            await displayActivitySensorData(sensor);
        } else if (sensor.sensor_type === 'temperature_humidity' || sensor.sensor_type === 'temp_humidity') {
            console.log('🌡️ Displaying environmental sensor data');
            await displayEnvironmentalSensorData(sensor);
        } else if (sensor.sensor_type === 'card_reader' || !sensor.sensor_type) {
            console.log('💳 Displaying card-reader (time-tracking) data');
            await displayCardReaderData(sensor);
        } else if (sensor.sensor_type === 'tracker' || sensor.sensor_type === 'gateway') {
            console.log('📍 Displaying tracker (GPS) data');
            await displayTrackerSensorData(sensor);
        } else {
            console.log('⚠️ Unsupported sensor type:', sensor.sensor_type);
            display.innerHTML = `
                <div class="text-center py-16 text-gray-500">
                    <p class="text-sm">Unsupported sensor type: ${sensor.sensor_type}</p>
                </div>
            `;
        }

    } catch (error) {
        console.error('❌ Error loading sensor data:', error);
        display.innerHTML = `
            <div class="text-center py-16 text-red-600">
                <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="mt-4 text-sm">Failed to load sensor data</p>
            </div>
        `;
    }
};

// State management for environmental sensor view
const envSensorState = {
    currentSensor: null,
    currentPeriod: 'weekly', // Default to weekly to show more data
    selectedDate: new Date(),
    chartInstances: [],
    currentData: null
};

/**
 * Format date range for environmental sensor display
 */
function formatEnvDateRange() {
    const date = new Date(envSensorState.selectedDate);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    
    if (envSensorState.currentPeriod === 'daily') {
        return date.toLocaleDateString('en-US', options);
    } else if (envSensorState.currentPeriod === '3day') {
        // Show 3 days: 2 days ago to selected date
        const startDate = new Date(date);
        startDate.setDate(startDate.getDate() - 2);
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${date.toLocaleDateString('en-US', options)}`;
    } else if (envSensorState.currentPeriod === 'weekly') {
        // Show full week: Monday to Sunday
        const dayOfWeek = date.getDay();
        const monday = new Date(date);
        monday.setDate(monday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);
        return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', options)}`;
    } else {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
}

/* ===================================================================== */
/* Card reader (time-tracking) sensor chart                              */
/* ===================================================================== */

const cardReaderState = {
    currentSensor: null,
    currentLocation: null,
    currentPeriod: 'daily',     // 'daily' | '3day' | 'weekly' | 'monthly'
    selectedDate: new Date(),
    chartInstance: null
};

/**
 * Compute [startDate, endDate] (both Date objects, inclusive) for the
 * currently selected period.
 */
function computeCardReaderRange() {
    const d = new Date(cardReaderState.selectedDate);
    d.setHours(0, 0, 0, 0);
    let start = new Date(d);
    let end = new Date(d);
    end.setHours(23, 59, 59, 999);

    if (cardReaderState.currentPeriod === '3day') {
        start = new Date(d);
        start.setDate(start.getDate() - 2);
    } else if (cardReaderState.currentPeriod === 'weekly') {
        // Monday to Sunday containing the selected date
        const dow = d.getDay(); // 0=Sun..6=Sat
        const offset = dow === 0 ? -6 : 1 - dow;
        start = new Date(d);
        start.setDate(start.getDate() + offset);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
    } else if (cardReaderState.currentPeriod === 'monthly') {
        start = new Date(d.getFullYear(), d.getMonth(), 1);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    return { start, end };
}

function formatCardReaderDateRange() {
    const { start, end } = computeCardReaderRange();
    const sameDay = start.toDateString() === end.toDateString();
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    if (sameDay) return start.toLocaleDateString('en-US', opts);
    if (cardReaderState.currentPeriod === 'monthly') {
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', opts)}`;
}

/**
 * Display card-reader sensor data with period selector, navigation and chart.
 */
async function displayCardReaderData(sensor) {
    const display = document.getElementById('sensor-data-display');
    if (!display) return;

    cardReaderState.currentSensor = sensor;

    const periodBtn = (period, label) => `
        <button onclick="window.changeCardReaderPeriod('${period}')"
                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    cardReaderState.currentPeriod === period
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }">
            ${label}
        </button>`;

    display.innerHTML = `
        <div>
            <h4 class="text-md font-medium text-gray-900 mb-4">Card Reader: ${sensor.name || sensor.sensor_id}</h4>

            <div class="mb-4">
                <div class="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div class="flex space-x-2">
                        ${periodBtn('daily', t('assets.24h') || '24h')}
                        ${periodBtn('3day', '3 Days')}
                        ${periodBtn('weekly', 'Weekly')}
                        ${periodBtn('monthly', 'Monthly')}
                    </div>
                    <div class="flex items-center space-x-2">
                        <button onclick="window.navigateCardReaderPeriod(-1)"
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <span id="card-reader-date-display" class="text-sm font-medium text-gray-700 min-w-[200px] text-center px-3 py-2 border border-gray-300 rounded-md bg-white">
                            ${formatCardReaderDateRange()}
                        </span>
                        <button onclick="window.navigateCardReaderPeriod(1)"
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        <button onclick="window.goToTodayCardReader()"
                                class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            Today
                        </button>
                    </div>
                </div>
            </div>

            <!-- Summary card -->
            <div class="grid grid-cols-1 gap-4 mb-4">
                <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                    <p class="text-xs font-medium text-blue-600 uppercase tracking-wide">Check-ins in this period</p>
                    <p id="card-reader-period-count" class="mt-2 text-4xl font-bold text-blue-900">–</p>
                    <p id="card-reader-period-sub" class="text-sm text-blue-700 mt-1">${formatCardReaderDateRange()}</p>
                </div>
            </div>

            <!-- Chart -->
            <div class="bg-white rounded-lg border border-gray-200 p-4" style="height: 420px;">
                <canvas id="card-reader-chart"></canvas>
            </div>
        </div>
    `;

    await loadCardReaderData();
}

async function loadCardReaderData() {
    const sensor = cardReaderState.currentSensor;
    if (!sensor) return;

    const { start, end } = computeCardReaderRange();
    const params = new URLSearchParams();
    const locId = locationInsightsState.selectedLocation?.id;
    if (locId) {
        params.append('location_id', locId);
    } else {
        params.append('sensor_id', sensor.sensor_id || sensor.id);
    }
    params.append('start_date', start.toISOString());
    params.append('end_date', end.toISOString());
    params.append('limit', '5000');

    try {
        const response = await fetch(`${API_BASE_URL}/time-tracking/events?${params.toString()}`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const data = await response.json();
        const events = data.events || data || [];

        renderCardReaderChart(events, start, end);
    } catch (err) {
        console.error('[CARD READER] load failed', err);
        const countEl = document.getElementById('card-reader-period-count');
        if (countEl) countEl.textContent = 'Error';
    }
}

function renderCardReaderChart(events, start, end) {
    const canvas = document.getElementById('card-reader-chart');
    if (!canvas) return;
    if (cardReaderState.chartInstance) {
        cardReaderState.chartInstance.destroy();
        cardReaderState.chartInstance = null;
    }

    // Bin events by check_in_time
    const period = cardReaderState.currentPeriod;
    const useHourBuckets = period !== 'monthly';

    const buckets = [];
    const labels = [];
    if (useHourBuckets) {
        // Hourly buckets across the entire range (1h, 3d, or 7d worth of hours)
        const cursor = new Date(start);
        cursor.setMinutes(0, 0, 0);
        const endHour = new Date(end);
        while (cursor <= endHour) {
            buckets.push(0);
            if (period === 'daily') {
                labels.push(String(cursor.getHours()).padStart(2, '0') + ':00');
            } else {
                labels.push(
                    cursor.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) +
                    ' ' + String(cursor.getHours()).padStart(2, '0') + ':00'
                );
            }
            cursor.setHours(cursor.getHours() + 1);
        }
    } else {
        const cursor = new Date(start);
        cursor.setHours(0, 0, 0, 0);
        const endDay = new Date(end);
        endDay.setHours(0, 0, 0, 0);
        while (cursor <= endDay) {
            buckets.push(0);
            labels.push(cursor.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));
            cursor.setDate(cursor.getDate() + 1);
        }
    }

    let total = 0;
    events.forEach(ev => {
        const t = ev.check_in_time || ev.checkIn || null;
        if (!t) return;
        const d = new Date(t);
        if (isNaN(d.getTime())) return;
        if (d < start || d > end) return;
        let idx;
        if (useHourBuckets) {
            const startHour = new Date(start);
            startHour.setMinutes(0, 0, 0);
            const dHour = new Date(d);
            dHour.setMinutes(0, 0, 0);
            idx = Math.round((dHour - startHour) / (3600 * 1000));
        } else {
            const startMid = new Date(start);
            startMid.setHours(0, 0, 0, 0);
            const dayMid = new Date(d);
            dayMid.setHours(0, 0, 0, 0);
            idx = Math.round((dayMid - startMid) / (24 * 3600 * 1000));
        }
        if (idx >= 0 && idx < buckets.length) {
            buckets[idx]++;
            total++;
        }
    });

    // Update summary
    const countEl = document.getElementById('card-reader-period-count');
    if (countEl) countEl.textContent = total.toLocaleString();
    const subEl = document.getElementById('card-reader-period-sub');
    if (subEl) subEl.textContent = formatCardReaderDateRange();
    const dateDisp = document.getElementById('card-reader-date-display');
    if (dateDisp) dateDisp.textContent = formatCardReaderDateRange();

    const ctx = canvas.getContext('2d');
    cardReaderState.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
            labels,
            datasets: [{
                label: 'Check-ins',
                data: buckets,
                backgroundColor: 'rgba(59, 130, 246, 0.6)',
                borderColor: 'rgba(59, 130, 246, 1)',
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.8
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (c) => `Check-ins: ${c.parsed.y}`
                    }
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        maxRotation: period === 'daily' ? 0 : 45,
                        minRotation: period === 'daily' ? 0 : 45,
                        font: { size: 10 },
                        autoSkip: true,
                        maxTicksLimit: period === 'daily' ? 12 : (useHourBuckets ? 24 : 16)
                    }
                },
                y: {
                    beginAtZero: true,
                    title: { display: true, text: 'Check-ins', font: { size: 12, weight: 'bold' } },
                    ticks: {
                        precision: 0,
                        callback: (v) => Number.isInteger(v) ? v : ''
                    }
                }
            }
        }
    });
}

window.changeCardReaderPeriod = async function(period) {
    cardReaderState.currentPeriod = period;
    if (cardReaderState.currentSensor) {
        await displayCardReaderData(cardReaderState.currentSensor);
    }
};

window.navigateCardReaderPeriod = async function(direction) {
    const d = new Date(cardReaderState.selectedDate);
    if (cardReaderState.currentPeriod === 'daily') d.setDate(d.getDate() + direction);
    else if (cardReaderState.currentPeriod === '3day') d.setDate(d.getDate() + direction * 3);
    else if (cardReaderState.currentPeriod === 'weekly') d.setDate(d.getDate() + direction * 7);
    else d.setMonth(d.getMonth() + direction);
    cardReaderState.selectedDate = d;
    await loadCardReaderData();
};

window.goToTodayCardReader = async function() {
    cardReaderState.selectedDate = new Date();
    await loadCardReaderData();
};

/**
 * Activity sensor state (chart + selected period/date)
 */
const activitySensorState = {
    currentSensor: null,
    currentAssetId: null,
    activityData: null,
    chartInstance: null,
    currentPeriod: 'weekly',
    selectedDate: new Date()
};

/**
 * Display activity sensor data – ports the legacy asset-insights activity
 * chart into the unified insights module. The activity REST endpoints are
 * asset-scoped (`/assets/{asset_id}/activity/...`), so this only works when
 * the selected entity has an `_id` (i.e. it's an asset).
 */
async function displayActivitySensorData(sensor) {
    const display = document.getElementById('sensor-data-display');
    if (!display) return;

    activitySensorState.currentSensor = sensor;
    const selected = locationInsightsState.selectedLocation;
    const assetId = selected && (selected._id || selected.id);
    activitySensorState.currentAssetId = assetId;

    display.innerHTML = `
        <div>
            <h4 class="text-md font-medium text-gray-900 mb-4">
                ${t('assets.activitySensor') || 'Activity Sensor'}: ${sensor.name || sensor.sensor_id}
            </h4>
            <div id="activity-chart-container">
                <div class="text-center py-16">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p class="mt-2 text-sm text-gray-500">${t('common.loading') || 'Loading'}...</p>
                </div>
            </div>
        </div>
    `;

    if (!assetId) {
        const container = document.getElementById('activity-chart-container');
        if (container) {
            container.innerHTML = `
                <div class="text-center py-16 text-gray-500">
                    <p class="text-sm">Activity charts are only available for assets.</p>
                </div>
            `;
        }
        return;
    }

    await loadActivityData(assetId);
}

/**
 * Load activity summary + timeseries for the selected asset and render chart
 */
async function loadActivityData(assetId) {
    const container = document.getElementById('activity-chart-container');
    if (!container) return;

    container.innerHTML = `
        <div class="text-center py-16">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-500">Loading activity data...</p>
        </div>
    `;

    try {
        const summaryResponse = await fetch(`${API_BASE_URL}/assets/${assetId}/activity/summary`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        if (!summaryResponse.ok) throw new Error('Failed to load activity summary');
        const summary = await summaryResponse.json();

        const year = activitySensorState.selectedDate.getFullYear();
        const month = String(activitySensorState.selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(activitySensorState.selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const timeseriesResponse = await fetch(
            `${API_BASE_URL}/assets/${assetId}/activity/timeseries?period=${activitySensorState.currentPeriod}&date=${dateStr}`,
            { headers: { 'Authorization': `Bearer ${auth.getToken()}` } }
        );
        if (!timeseriesResponse.ok) throw new Error('Failed to load activity timeseries');
        const timeseries = await timeseriesResponse.json();

        activitySensorState.activityData = { summary, timeseries };
        renderActivityChart();
    } catch (error) {
        console.error('Error loading activity data:', error);
        container.innerHTML = `
            <div class="text-center py-16 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p class="mt-4 text-sm">No activity data available for this asset</p>
            </div>
        `;
    }
}

/**
 * Format date range for the activity chart header / date picker display
 */
function formatActivityDateRange() {
    const date = activitySensorState.selectedDate;
    if (activitySensorState.currentPeriod === 'daily') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } else if (activitySensorState.currentPeriod === '3day') {
        const endDate = new Date(date);
        const startDate = new Date(date);
        startDate.setDate(startDate.getDate() - 2);
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (activitySensorState.currentPeriod === 'weekly') {
        const day = date.getDay();
        const daysFromMonday = day === 0 ? 6 : day - 1;
        const startDate = new Date(date);
        startDate.setDate(startDate.getDate() - daysFromMonday);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);
        return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else { // monthly
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
}

/**
 * Format timestamp for chart labels
 */
function formatActivityTimestamp(timestamp, period) {
    const date = new Date(timestamp);
    if (period === 'daily' || period === '3day' || period === 'weekly') {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Render the activity chart and surrounding period/date controls
 */
function renderActivityChart() {
    const container = document.getElementById('activity-chart-container');
    if (!container || !activitySensorState.activityData) return;

    const { summary, timeseries } = activitySensorState.activityData;

    // Period total
    const periodTotal = (timeseries.data_points || []).reduce((sum, point) => {
        if (point.duration_hours !== undefined) return sum + point.duration_hours;
        if (point.duration_minutes !== undefined) return sum + (point.duration_minutes / 60);
        if (point.duration_seconds !== undefined) return sum + (point.duration_seconds / 3600);
        return sum;
    }, 0);

    const year = activitySensorState.selectedDate.getFullYear();
    const month = String(activitySensorState.selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(activitySensorState.selectedDate.getDate()).padStart(2, '0');
    const dateInputValue = `${year}-${month}-${day}`;

    const periodBtn = (key, label) => `
        <button onclick="window.changeActivityPeriod('${key}')"
                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activitySensorState.currentPeriod === key
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }">
            ${label}
        </button>
    `;

    container.innerHTML = `
        <div class="mb-6">
            <div class="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div class="flex space-x-2">
                    ${periodBtn('daily', t('assets.24h') || '24h')}
                    ${periodBtn('3day', t('assets.threeDays') || '3 days')}
                    ${periodBtn('weekly', t('assets.weekly') || 'Weekly')}
                    ${periodBtn('monthly', t('assets.monthly') || 'Monthly')}
                </div>
                <div class="flex items-center space-x-2">
                    <button onclick="window.navigateActivityPeriod(-1)"
                            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div class="relative">
                        <button type="button" id="activity-date-picker-button"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors min-w-[200px] justify-between">
                            <span id="activity-date-display">${formatActivityDateRange()}</span>
                            <svg class="ml-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                            </svg>
                        </button>
                        <input type="hidden" id="activity-date-picker-input" value="${dateInputValue}">
                        <div id="activity-calendar-popup" class="hidden absolute left-1/2 transform -translate-x-1/2 z-50 mt-2 bg-white rounded-lg shadow-lg border border-gray-200"></div>
                    </div>
                    <button onclick="window.navigateActivityPeriod(1)"
                            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    <button onclick="window.goToTodayActivity()"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                        Today
                    </button>
                    <button onclick="window.refreshActivityData()"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 transition-colors">
                        Refresh
                    </button>
                </div>
            </div>
        </div>

        <div class="bg-white rounded-lg border border-gray-200 p-4" style="height: 500px;">
            <canvas id="activity-chart"></canvas>
        </div>

        <div class="grid grid-cols-2 gap-6 mt-6">
            <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                <p class="text-xs font-medium text-blue-600 uppercase tracking-wide">${t('assets.totalActivityAllTime') || 'Total activity (all time)'}</p>
                <p class="mt-2 text-4xl font-bold text-blue-900">${(summary.total_activity_hours || 0).toFixed(1)}</p>
                <p class="text-sm text-blue-700 mt-1">${summary.reading_count || 0} ${t('assets.readings') || 'readings'}</p>
            </div>
            <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200">
                <p class="text-xs font-medium text-green-600 uppercase tracking-wide">${t('assets.activitySelectedPeriod') || 'Activity (selected period)'}</p>
                <p class="mt-2 text-4xl font-bold text-green-900">${periodTotal.toFixed(1)}</p>
                <p class="text-sm text-green-700 mt-1">${formatActivityDateRange()}</p>
            </div>
        </div>
    `;

    renderActivityChartCanvas(timeseries);
    setTimeout(() => initializeActivityCalendarPicker(), 100);
}

/**
 * Render the activity bar chart on the canvas
 */
function renderActivityChartCanvas(timeseries) {
    const canvas = document.getElementById('activity-chart');
    if (!canvas) return;

    if (activitySensorState.chartInstance) {
        activitySensorState.chartInstance.destroy();
    }

    const ctx = canvas.getContext('2d');
    const period = activitySensorState.currentPeriod;
    const useMinutes = period === 'daily' || period === '3day' || period === 'weekly';

    const labels = (timeseries.data_points || []).map(item => {
        const ts = typeof item.timestamp === 'string' ? item.timestamp :
                   item.timestamp?.$date ? item.timestamp.$date : item.timestamp;
        return formatActivityTimestamp(ts, period);
    });

    const data = (timeseries.data_points || []).map(item => {
        let hours = 0;
        if (item.duration_hours !== undefined) hours = item.duration_hours;
        else if (item.duration_minutes !== undefined) hours = item.duration_minutes / 60;
        else if (item.duration_seconds !== undefined) hours = item.duration_seconds / 3600;
        return useMinutes ? hours * 60 : hours;
    });

    activitySensorState.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
            labels: labels,
            datasets: [{
                label: useMinutes ? 'Activity Minutes' : 'Activity Hours',
                data: data,
                backgroundColor: 'rgba(59, 130, 246, 0.6)',
                borderColor: 'rgba(59, 130, 246, 1)',
                borderWidth: 1,
                borderRadius: 4,
                barPercentage: 0.8
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            const value = context.parsed.y;
                            if (useMinutes) return `Activity: ${Math.round(value)}m`;
                            const hours = Math.floor(value);
                            const minutes = Math.round((value % 1) * 60);
                            return `Activity: ${hours}h ${minutes}m`;
                        }
                    }
                }
            },
            scales: {
                x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 } } },
                y: {
                    beginAtZero: true,
                    min: 0,
                    max: useMinutes ? (period === 'daily' ? 10 : 60) : undefined,
                    title: { display: true, text: useMinutes ? 'Minutes' : 'Hours', font: { size: 12, weight: 'bold' } },
                    ticks: {
                        callback: function(value) {
                            if (useMinutes) return Math.round(value) + 'm';
                            return value.toFixed(1) + 'h';
                        }
                    }
                }
            }
        }
    });
}

/**
 * Calendar picker on activity chart
 */
function initializeActivityCalendarPicker() {
    const dateInput = document.getElementById('activity-date-picker-input');
    const button = document.getElementById('activity-date-picker-button');
    if (!dateInput || !button) return;

    createCalendarPicker({
        inputId: 'activity-date-picker-input',
        buttonId: 'activity-date-picker-button',
        displayId: 'activity-date-display',
        popupId: 'activity-calendar-popup',
        isRange: false,
        defaultDate: activitySensorState.selectedDate,
        onChange: async (dates) => {
            if (dates && dates.startDate) {
                activitySensorState.selectedDate = dates.startDate;
                const display = document.getElementById('activity-date-display');
                if (display) display.textContent = formatActivityDateRange();
                if (activitySensorState.currentAssetId) {
                    await loadActivityData(activitySensorState.currentAssetId);
                }
            }
        }
    });
}

// Activity chart control handlers
window.changeActivityPeriod = async function(period) {
    activitySensorState.currentPeriod = period;
    if (activitySensorState.currentAssetId) {
        await loadActivityData(activitySensorState.currentAssetId);
    }
};

window.navigateActivityPeriod = async function(direction) {
    const date = new Date(activitySensorState.selectedDate);
    const period = activitySensorState.currentPeriod;
    if (period === 'daily') date.setDate(date.getDate() + direction);
    else if (period === '3day') date.setDate(date.getDate() + direction * 3);
    else if (period === 'weekly') date.setDate(date.getDate() + direction * 7);
    else date.setMonth(date.getMonth() + direction);
    activitySensorState.selectedDate = date;
    if (activitySensorState.currentAssetId) {
        await loadActivityData(activitySensorState.currentAssetId);
    }
};

window.goToTodayActivity = async function() {
    activitySensorState.selectedDate = new Date();
    if (activitySensorState.currentAssetId) {
        await loadActivityData(activitySensorState.currentAssetId);
    }
};

window.refreshActivityData = async function() {
    if (activitySensorState.currentAssetId) {
        await loadActivityData(activitySensorState.currentAssetId);
        showSuccess('Activity data refreshed');
    }
};

/**
 * Display environmental sensor data
 */
async function displayEnvironmentalSensorData(sensor) {
    const display = document.getElementById('sensor-data-display');
    if (!display) return;

    // Store sensor in state
    envSensorState.currentSensor = sensor;
    
    console.log('🎨 Rendering environmental sensor display');
    
    display.innerHTML = `
        <div>
            <!-- Export buttons -->
            <div class="flex justify-end gap-2 mb-3">
                <button data-env-export onclick="window.generateEnvSensorPDF()" disabled
                        class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm opacity-50 cursor-not-allowed">
                    <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    ${t('insights.exportPdf')}
                </button>
                <button data-env-export onclick="window.exportEnvSensorToExcel()" disabled
                        class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm opacity-50 cursor-not-allowed">
                    <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    ${t('insights.exportExcel')}
                </button>
            </div>

            <!-- Period Selection and Date Navigation -->
            <div class="mb-6">
                <div class="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <!-- Period Buttons -->
                    <div class="flex space-x-2">
                        <button onclick="window.changeEnvPeriod('daily')" 
                                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    envSensorState.currentPeriod === 'daily' 
                                        ? 'bg-primary-600 text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }">
                            ${t('assets.24h')}
                        </button>
                        <button onclick="window.changeEnvPeriod('3day')" 
                                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    envSensorState.currentPeriod === '3day' 
                                        ? 'bg-primary-600 text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }">
                            3 Days
                        </button>
                        <button onclick="window.changeEnvPeriod('weekly')" 
                                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    envSensorState.currentPeriod === 'weekly' 
                                        ? 'bg-primary-600 text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }">
                            Weekly
                        </button>
                        <button onclick="window.changeEnvPeriod('monthly')" 
                                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    envSensorState.currentPeriod === 'monthly' 
                                        ? 'bg-primary-600 text-white' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }">
                            Monthly
                        </button>
                    </div>
                    
                    <!-- Date Navigation -->
                    <div class="flex items-center space-x-2">
                        <!-- Previous Period Button -->
                        <button onclick="window.navigateEnvPeriod(-1)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        
                        <!-- Date Picker -->
                        <div class="relative">
                            <button type="button" id="env-date-picker-button"
                                    class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors min-w-[200px] justify-between">
                                <span id="env-date-display">Loading...</span>
                                <svg class="ml-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                                </svg>
                            </button>
                            <input type="hidden" id="env-date-picker-input" value="">
                            <div id="env-calendar-popup" class="hidden absolute left-1/2 transform -translate-x-1/2 z-50 mt-2 bg-white rounded-lg shadow-lg border border-gray-200"></div>
                        </div>
                        
                        <!-- Next Period Button -->
                        <button onclick="window.navigateEnvPeriod(1)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        
                        <!-- Today Button -->
                        <button onclick="window.goToTodayEnv()" 
                                class="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            Today
                        </button>
                    </div>
                </div>
            </div>

            <!-- Charts Container -->
            <div id="env-charts-container">
                <div class="text-center py-8 text-gray-500">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p class="mt-4 text-sm">Loading data...</p>
                </div>
            </div>
        </div>
    `;

    // Load initial data
    await loadEnvironmentalData();
}

/**
 * Load and display environmental sensor data with charts
 */
async function loadEnvironmentalData() {
    const container = document.getElementById('env-charts-container');
    if (!container || !envSensorState.currentSensor) return;

    container.innerHTML = `
        <div class="text-center py-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-4 text-sm text-gray-500">Loading environmental data...</p>
        </div>
    `;

    try {
        const sensor = envSensorState.currentSensor;
        const year = envSensorState.selectedDate.getFullYear();
        const month = String(envSensorState.selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(envSensorState.selectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        // Calculate date range based on period
        let startDate, endDate;
        const selectedDate = new Date(dateStr);
        
        if (envSensorState.currentPeriod === 'daily') {
            startDate = dateStr;
            endDate = dateStr;
        } else if (envSensorState.currentPeriod === '3day') {
            const threeDaysAgo = new Date(selectedDate);
            threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
            startDate = threeDaysAgo.toISOString().split('T')[0];
            endDate = dateStr;
        } else if (envSensorState.currentPeriod === 'weekly') {
            const dayOfWeek = selectedDate.getDay();
            const monday = new Date(selectedDate);
            monday.setDate(monday.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
            const sunday = new Date(monday);
            sunday.setDate(sunday.getDate() + 6);
            startDate = monday.toISOString().split('T')[0];
            endDate = sunday.toISOString().split('T')[0];
        } else { // monthly
            const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
            startDate = firstDay.toISOString().split('T')[0];
            endDate = lastDay.toISOString().split('T')[0];
        }

        // Fetch sensor logs
        console.log(`Fetching sensor logs for sensor ${sensor.sensor_id} from ${startDate} to ${endDate}`);
        const logsResponse = await fetch(
            `${API_BASE_URL}/sensor-logs?sensor_id=${sensor.sensor_id}&start_date=${startDate}&end_date=${endDate}&limit=500&sort_order=asc`,
            {
                headers: {
                    'Authorization': `Bearer ${auth.getToken()}`
                }
            }
        );

        if (!logsResponse.ok) {
            throw new Error('Failed to fetch sensor logs');
        }

        const logsData = await logsResponse.json();
        const logs = logsData.logs || [];
        console.log(`Received ${logs.length} sensor logs`);
        console.log('First log sample:', logs[0]);

        if (logs.length === 0) {
            envSensorState.currentData = null;
            document.querySelectorAll('[data-env-export]').forEach(btn => {
                btn.disabled = true;
                btn.classList.add('opacity-50', 'cursor-not-allowed');
            });
            container.innerHTML = `
                <div class="text-center py-8 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p class="mt-4 text-sm">No data found for the selected period</p>
                </div>
            `;
            return;
        }

        // Extract data points with formatted labels
        const labels = [];
        const timestamps = []; // Store actual timestamps for annotations
        const temperatures = [];
        const humidities = [];
        const pressures = [];

        logs.forEach(log => {
            // Environmental data is in raw_data.environmental_data array
            if (log.raw_data && log.raw_data.environmental_data) {
                const envDataArray = log.raw_data.environmental_data;
                
                envDataArray.forEach(reading => {
                    const timestamp = new Date(reading.ts * 1000); // Convert Unix timestamp to JS Date
                    timestamps.push(timestamp);
                    
                    // Format label based on period (24-hour format for European market)
                    let label;
                    if (envSensorState.currentPeriod === 'daily') {
                        label = timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                    } else if (envSensorState.currentPeriod === 'monthly') {
                        label = timestamp.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
                    } else {
                        // For 3-day and weekly views, show only time (day is marked by vertical lines)
                        label = timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                    }
                    
                    labels.push(label);
                    temperatures.push(reading.ambient_temperature || null);
                    humidities.push(reading.relative_humidity || null);
                    pressures.push(reading.atmospheric_pressure ? reading.atmospheric_pressure / 100 : null);
                });
            }
        });
        
        console.log(`Extracted ${labels.length} data points from environmental_data arrays`);

        // Keep the current data points + context in state so the Excel/PDF export
        // buttons can act on exactly what is on screen.
        const hasPressure = sensor.sensor_type === 'temperature_humidity'
            && pressures.some(p => p != null);
        envSensorState.currentData = {
            timestamps,
            temperatures,
            humidities,
            pressures,
            hasPressure,
            period: envSensorState.currentPeriod,
            rangeLabel: formatEnvDateRange(),
            startDate,
            endDate,
        };
        // Enable the export buttons now that data is available
        document.querySelectorAll('[data-env-export]').forEach(btn => {
            btn.disabled = false;
            btn.classList.remove('opacity-50', 'cursor-not-allowed');
        });

        // Render three synchronized, zoomable charts (uPlot) that share one
        // cursor and x-zoom range. Day separators and y-ranges are handled
        // inside the chart module.
        renderSyncedEnvCharts(container, {
            timestamps,
            temperatures,
            humidities,
            pressures,
            period: envSensorState.currentPeriod,
        });
        
        // Initialize calendar picker after charts are rendered
        setTimeout(() => {
            initializeEnvCalendarPicker();
        }, 100);

    } catch (error) {
        console.error('Error loading environmental data:', error);
        container.innerHTML = `
            <div class="text-center py-8 text-red-600">
                <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="mt-4 text-sm">${error.message}</p>
            </div>
        `;
    }
}

/**
 * Change environmental sensor period
 */
window.changeEnvPeriod = async function(period) {
    envSensorState.currentPeriod = period;
    if (envSensorState.currentSensor) {
        await displayEnvironmentalSensorData(envSensorState.currentSensor);
    }
};

/**
 * Build the export context from the current environmental sensor view.
 */
function buildEnvExportContext() {
    return {
        sensor: envSensorState.currentSensor,
        locationName: locationInsightsState.selectedLocation?.name || '',
        data: envSensorState.currentData,
        chartsContainerId: 'env-charts-container',
    };
}

window.exportEnvSensorToExcel = function() {
    exportEnvSensorToExcel(buildEnvExportContext());
};

window.generateEnvSensorPDF = function() {
    generateEnvSensorPDF(buildEnvExportContext());
};

/**
 * Navigate environmental sensor period
 */
window.navigateEnvPeriod = async function(direction) {
    const date = new Date(envSensorState.selectedDate);
    
    if (envSensorState.currentPeriod === 'daily') {
        date.setDate(date.getDate() + direction);
    } else if (envSensorState.currentPeriod === '3day') {
        date.setDate(date.getDate() + (direction * 3));
    } else if (envSensorState.currentPeriod === 'weekly') {
        date.setDate(date.getDate() + (direction * 7));
    } else { // monthly
        date.setMonth(date.getMonth() + direction);
    }
    
    envSensorState.selectedDate = date;
    
    // Update date picker display
    const display = document.getElementById('env-date-display');
    if (display) {
        display.textContent = formatEnvDateRange();
    }
    
    const input = document.getElementById('env-date-picker-input');
    if (input) {
        input.value = date.toISOString().split('T')[0];
    }
    
    if (envSensorState.currentSensor) {
        await loadEnvironmentalData();
    }
};

/**
 * Go to today for environmental sensor
 */
window.goToTodayEnv = async function() {
    envSensorState.selectedDate = new Date();
    
    // Update date picker display
    const display = document.getElementById('env-date-display');
    if (display) {
        display.textContent = formatEnvDateRange();
    }
    
    const input = document.getElementById('env-date-picker-input');
    if (input) {
        input.value = new Date().toISOString().split('T')[0];
    }
    
    if (envSensorState.currentSensor) {
        await loadEnvironmentalData();
    }
};

/**
 * Initialize environmental calendar picker
 */
function initializeEnvCalendarPicker() {
    const dateInput = document.getElementById('env-date-picker-input');
    const button = document.getElementById('env-date-picker-button');
    const display = document.getElementById('env-date-display');
    
    if (!dateInput || !button || !display) {
        console.log('Date picker elements not found:', { dateInput: !!dateInput, button: !!button, display: !!display });
        return;
    }
    
    // Set initial values
    dateInput.value = new Date().toISOString().split('T')[0];
    display.textContent = formatEnvDateRange();
    
    // Import createCalendarPicker if not already available
    if (typeof createCalendarPicker === 'undefined') {
        console.warn('createCalendarPicker not available, skipping calendar initialization');
        return;
    }
    
    console.log('Initializing environmental calendar picker');
    
    // Create calendar picker
    createCalendarPicker({
        inputId: 'env-date-picker-input',
        buttonId: 'env-date-picker-button',
        displayId: 'env-date-display',
        popupId: 'env-calendar-popup',
        isRange: false,
        defaultDate: envSensorState.selectedDate,
        onChange: async (dates) => {
            if (dates && dates.startDate) {
                envSensorState.selectedDate = dates.startDate;
                
                // Update display
                const display = document.getElementById('env-date-display');
                if (display) {
                    display.textContent = formatEnvDateRange();
                }
                
                // Reload data
                if (envSensorState.currentSensor) {
                    await loadEnvironmentalData();
                }
            }
        }
    });
}

/**
 * Initialize Google Maps
 */
async function initializeMyLocationsMap() {
    // Check if user is still logged in
    if (!auth.isAuthenticated()) {
        console.log('❌ User not logged in, aborting map initialization');
        return;
    }
    
    if (typeof google === 'undefined') {
        console.error('Google Maps not loaded');
        return;
    }

    const mapElement = document.getElementById('locations-map');
    if (!mapElement) return;

    // Create map centered on Denmark (default)
    myLocationsMap = new google.maps.Map(mapElement, {
        center: { lat: 56.26392, lng: 9.501785 },
        zoom: 7,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true
    });

    // Initialize geocoder
    googleGeocoder = new google.maps.Geocoder();

    // Function to try using work address
    const tryWorkAddress = () => {
        const currentUser = getCurrentUser();
        
        if (currentUser && currentUser.work_address) {
            console.log('Using work address:', currentUser.work_address);
            
            // Geocode work address
            googleGeocoder.geocode({ address: currentUser.work_address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const workPos = results[0].geometry.location;
                    
                    // Add work address marker
                    const workMarker = new google.maps.Marker({
                        position: workPos,
                        map: myLocationsMap,
                        title: 'Your Work Address',
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: '#10b981',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2
                        }
                    });

                    const infoWindow = new google.maps.InfoWindow({
                        content: `<b>Your Work Address</b><br><span class="text-xs text-gray-500">${currentUser.work_address}</span>`
                    });
                    infoWindow.open(myLocationsMap, workMarker);

                    myLocationsMap.setCenter(workPos);
                    myLocationsMap.setZoom(12);
                } else {
                    console.log('Could not geocode work address');
                }
                
                setupMapListeners();
                loadLocationsInView();
            });
        } else {
            // No work address, just use default center
            console.log('No work address set, using default center');
            setupMapListeners();
            loadLocationsInView();
        }
    };

    // Try to get user's current location using browser geolocation (Chrome uses Google Location Services)
    console.log('🌍 Requesting geolocation...');
    
    // Check permission status first
    if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
            console.log('Geolocation permission status:', result.state);
            if (result.state === 'denied') {
                console.warn('⚠️ Location permission denied by user. Using work address.');
                tryWorkAddress();
                return;
            }
        });
    }
    
    // Check if we have a cached accurate position in localStorage
    const cachedPosition = localStorage.getItem('user_geolocation');
    if (cachedPosition) {
        try {
            const cached = JSON.parse(cachedPosition);
            const age = Date.now() - cached.timestamp;
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
            
            // Use cached position if it's recent and accurate
            if (age < maxAge && cached.accuracy < 10000) {
                console.log(`📍 Using cached position from ${Math.round(age / 1000 / 60)} minutes ago (accuracy: ${Math.round(cached.accuracy)}m)`);
                
                const userPos = { lat: cached.lat, lng: cached.lng };
                
                userLocationMarker = new google.maps.Circle({
                    map: myLocationsMap,
                    center: userPos,
                    radius: Math.max(cached.accuracy, 50),
                    fillColor: '#3b82f6',
                    fillOpacity: 0.2,
                    strokeColor: '#3b82f6',
                    strokeWeight: 2
                });

                const userMarker = new google.maps.Marker({
                    position: userPos,
                    map: myLocationsMap,
                    title: 'Your Current Location',
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#3b82f6',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2
                    }
                });

                const infoWindow = new google.maps.InfoWindow({
                    content: `<b>Your Current Location</b><br><span class="text-xs text-gray-500">${Math.round(cached.accuracy)}m accuracy (cached)</span>`
                });
                infoWindow.open(myLocationsMap, userMarker);

                myLocationsMap.setCenter(userPos);
                myLocationsMap.setZoom(12);
                
                // Wait for map to be ready before loading locations
                google.maps.event.addListenerOnce(myLocationsMap, 'idle', () => {
                    setupMapListeners();
                    loadLocationsInView();
                });
                
                return; // Use cached position, don't request new one
            } else {
                console.log('Cached position expired or inaccurate, requesting fresh position...');
                localStorage.removeItem('user_geolocation');
            }
        } catch (e) {
            console.log('Error parsing cached position:', e);
        }
    }
    
    if (navigator.geolocation) {
        console.log('🌍 Requesting geolocation (may take up to 30 seconds for WiFi scanning)...');
        
        navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userLat = position.coords.latitude;
                    const userLng = position.coords.longitude;
                    const accuracy = position.coords.accuracy;
                    const userPos = { lat: userLat, lng: userLng };

                    console.log(`📍 Geolocation result: ${userLat.toFixed(6)}, ${userLng.toFixed(6)} (accuracy: ${Math.round(accuracy)}m)`);

                    // Accept reasonable accuracy (< 10km)
                    if (accuracy < 10000) {
                        console.log(`✅ Accepted position with ${Math.round(accuracy)}m accuracy`);
                        
                        // Cache the accurate position with source marker
                        localStorage.setItem('user_geolocation', JSON.stringify({
                            lat: userLat,
                            lng: userLng,
                            accuracy: accuracy,
                            timestamp: Date.now(),
                            source: 'geolocation' // Mark as real geolocation, not fallback
                        }));
                        console.log('💾 Cached position for future use');
                        
                        userLocationMarker = new google.maps.Circle({
                            map: myLocationsMap,
                            center: userPos,
                            radius: Math.max(accuracy, 50),
                            fillColor: '#3b82f6',
                            fillOpacity: 0.2,
                            strokeColor: '#3b82f6',
                            strokeWeight: 2
                        });

                        const userMarker = new google.maps.Marker({
                            position: userPos,
                            map: myLocationsMap,
                            title: 'Your Current Location',
                            icon: {
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: '#3b82f6',
                                fillOpacity: 1,
                                strokeColor: '#ffffff',
                                strokeWeight: 2
                            }
                        });

                        const infoWindow = new google.maps.InfoWindow({
                            content: `<b>Your Current Location</b><br><span class="text-xs text-gray-500">${Math.round(accuracy)}m accuracy</span>`
                        });
                        infoWindow.open(myLocationsMap, userMarker);

                        myLocationsMap.setCenter(userPos);
                        myLocationsMap.setZoom(12);
                        
                        // Wait for map to be ready before loading locations
                        google.maps.event.addListenerOnce(myLocationsMap, 'idle', () => {
                            setupMapListeners();
                            loadLocationsInView();
                        });
                    } else {
                        console.log(`⏳ Accuracy too low (${Math.round(accuracy)}m) - WiFi scanning may not be available on this system`);
                        console.log('Using work address fallback...');
                        localStorage.removeItem('user_geolocation');
                        tryWorkAddress();
                    }
                },
                (error) => {
                    console.log('Geolocation error:', error.code, error.message);
                    localStorage.removeItem('user_geolocation');
                    tryWorkAddress();
                },
                {
                    enableHighAccuracy: true,
                    timeout: 30000,
                    maximumAge: 0
                }
        );
    } else {
        tryWorkAddress();
        setupMapListeners();
        loadLocationsInView();
    }
}

/**
 * Setup Google Maps event listeners
 */
function setupMapListeners() {
    if (!myLocationsMap) return;
    
    google.maps.event.addListener(myLocationsMap, 'dragstart', () => {
        isUserInteracting = true;
    });
    
    google.maps.event.addListener(myLocationsMap, 'zoom_changed', () => {
        isUserInteracting = true;
    });
    
    google.maps.event.addListener(myLocationsMap, 'dragend', () => {
        triggerDelayedLoad();
    });
    
    google.maps.event.addListener(myLocationsMap, 'idle', () => {
        // Capture map bounds for use by the Data View 'map bounds' filter mode
        const b = myLocationsMap.getBounds();
        if (b) {
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            myLocationsLastMapBounds = {
                north: ne.lat(),
                east: ne.lng(),
                south: sw.lat(),
                west: sw.lng()
            };
        }
        if (isUserInteracting) {
            isUserInteracting = false;
            triggerDelayedLoad();
        }
    });
}

/**
 * Trigger delayed load of locations (debounced)
 */
function triggerDelayedLoad() {
    const bounds = myLocationsMap.getBounds();
    if (!bounds) return;
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    // Check if bounds have actually changed significantly
    if (lastLoadedBounds) {
        const oldNe = lastLoadedBounds.getNorthEast();
        const oldSw = lastLoadedBounds.getSouthWest();
        
        if (Math.abs(ne.lat() - oldNe.lat()) < 0.001 &&
            Math.abs(ne.lng() - oldNe.lng()) < 0.001 &&
            Math.abs(sw.lat() - oldSw.lat()) < 0.001 &&
            Math.abs(sw.lng() - oldSw.lng()) < 0.001) {
            return;
        }
    }
    
    if (mapMoveTimeout) {
        clearTimeout(mapMoveTimeout);
    }
    
    mapMoveTimeout = setTimeout(() => {
        loadLocationsInView();
    }, 300);
}

/**
 * Load locations visible in current map view
 */
async function loadLocationsInView() {
    if (!myLocationsMap) return;

    const currentUser = getCurrentUser();
    if (!currentUser) return;

    const loadingIndicator = document.getElementById('map-loading-indicator');
    if (loadingIndicator) {
        loadingIndicator.classList.remove('hidden');
    }

    const bounds = myLocationsMap.getBounds();
    if (!bounds) {
        console.log('Map bounds not ready yet');
        return;
    }
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Always cache the current map bounds so the Data View's 'mapBounds'
    // filter mode can reuse them — even on first render before the user
    // interacts with the map.
    myLocationsLastMapBounds = {
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng()
    };

    const locationsList = document.getElementById('locations-list');
    if (!locationsList) return; // Tab has been switched, abort
    
    locationsList.innerHTML = `
        <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="mt-2 text-sm text-gray-500">Loading locations...</p>
        </div>
    `;

    try {
        // Map View is a standalone filtering mechanism — it uses ONLY the
        // viewport bounds and does NOT inherit account / search filters from
        // the Data View tab.
        const params = new URLSearchParams();
        params.append('limit', '1000');
        params.append('north', ne.lat());
        params.append('south', sw.lat());
        params.append('east', ne.lng());
        params.append('west', sw.lng());

        const cfg = getEntityConfig();
        const url = `${API_BASE_URL}${cfg.listPath}?${params.toString()}`;

        const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });

        if (!response.ok) {
            throw new Error(`Failed to load ${cfg.labelPlural.toLowerCase()}`);
        }

        const data = await response.json();
        const locationsInView = data[cfg.responseKey] || data.locations || data.assets || [];
        const totalCount = data.total || 0;

        let userLat = null;
        let userLng = null;
        if (userLocationMarker) {
            const center = userLocationMarker.getCenter();
            userLat = center.lat();
            userLng = center.lng();
        }

        await displayLocationsOnMap(locationsInView, userLat, userLng);
        updateLocationCount(locationsInView.length, totalCount);
        lastLoadedBounds = myLocationsMap.getBounds();

    } catch (error) {
        console.error('Error loading locations:', error);
    } finally {
        if (loadingIndicator) {
            loadingIndicator.classList.add('hidden');
        }
    }
}

/**
 * Display locations on map and in list
 */
async function displayLocationsOnMap(locations, userLat = null, userLng = null) {
    const cfg = getEntityConfig();
    // Clear existing markers
    myLocationsMarkers.forEach(marker => marker.setMap(null));
    myLocationsMarkers = [];

    // Pre-compute entity positions so we don't re-extract repeatedly
    locations.forEach(entity => {
        entity._position = cfg.extractPosition(entity);
    });

    // Calculate distances if user location available
    if (userLat && userLng) {
        locations.forEach(entity => {
            const p = entity._position;
            if (p) {
                entity.distance = calculateDistance(userLat, userLng, p.lat, p.lng);
            }
        });
        locations.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    }

    // Marker icon by position source
    const markerIcon = (source) => {
        if (source === 'gps') return 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
        if (source === 'home_location') return 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
        return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
    };

    // Add markers
    locations.forEach(entity => {
        const p = entity._position;
        if (!p) return;
        const position = { lat: p.lat, lng: p.lng };

        const marker = new google.maps.Marker({
            position: position,
            map: myLocationsMap,
            title: entity.name,
            icon: { url: markerIcon(p.source) }
        });

        const distanceText = entity.distance
            ? `<br><small>${entity.distance.toFixed(1)} km away</small>`
            : '';

        const sourceLabel = p.source === 'gps'
            ? `<br><small style="color:#16a34a">Live GPS${p.timestamp ? ' • ' + new Date(p.timestamp).toLocaleString() : ''}</small>`
            : p.source === 'home_location'
                ? `<br><small style="color:#2563eb">Home location</small>`
                : '';

        const infoWindow = new google.maps.InfoWindow({
            content: `
                <div style="padding: 8px;">
                    <b>${entity.name}</b><br>
                    ${cfg.extractAddress(entity) || 'No address'}
                    ${sourceLabel}
                    ${distanceText}
                </div>
            `
        });

        marker.addListener('click', () => {
            infoWindow.open(myLocationsMap, marker);
        });

        myLocationsMarkers.push(marker);
    });

    displayMyLocationsList(locations);
}

/**
 * Update location count in header
 */
function updateLocationCount(shown, total) {
    const listContainer = document.querySelector('#locations-list');
    if (!listContainer) return; // Tab has been switched, abort
    
    const header = listContainer.parentElement.querySelector('h3');
    if (!header) return;
    
    if (shown === total) {
        header.textContent = `Nearby Locations (${total})`;
    } else {
        header.textContent = `Nearby Locations (showing ${shown} of ${total} - zoom in to see more)`;
    }
}

/**
 * Display locations list
 */
function displayMyLocationsList(locations) {
    const listElement = document.getElementById('locations-list');
    if (!listElement) return;

    if (locations.length === 0) {
        listElement.innerHTML = `
            <div class="text-center py-8 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p class="mt-2">${t('locations.noLocationsFound')}</p>
            </div>
        `;
        return;
    }

    listElement.innerHTML = `
        <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ${t('locations.location')}
                        </th>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ${t('locations.distance')}
                        </th>
                        <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ${t('locations.latestLog')}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    ${locations.map(location => {
                        const _p = location._position || getEntityConfig().extractPosition(location);
                        const hasCoordinates = !!_p;
                        const distanceText = location.distance 
                            ? `${location.distance.toFixed(1)} km` 
                            : '-';
                        const address = getEntityConfig().extractAddress(location);
                        
                        const latestLogHtml = renderLatestLogCell(location.latest_log);
                        
                        return `
                            <tr class="${hasCoordinates ? 'cursor-pointer hover:bg-gray-50' : ''}" 
                                ${hasCoordinates ? `onclick="focusLocation(${_p.lat}, ${_p.lng}, '${(location.name || '').replace(/'/g, "\\'")}')"` : ''}>
                                <td class="px-4 py-4 align-top">
                                    <div class="text-sm font-medium text-gray-900">${location.name}</div>
                                    ${address ? `<div class="text-xs text-gray-500">${address}</div>` : ''}
                                </td>
                                <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                                    ${distanceText}
                                </td>
                                <td class="px-4 py-4 align-top">
                                    ${latestLogHtml}
                                </td>
                            </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
        </div>
    `;
}

/**
 * Focus on a location on the map
 */
export function focusLocation(lat, lng, name) {
    if (myLocationsMap) {
        myLocationsMap.setCenter({ lat, lng });
        myLocationsMap.setZoom(15);
        
        myLocationsMarkers.forEach(marker => {
            const markerPos = marker.getPosition();
            if (Math.abs(markerPos.lat() - lat) < 0.00001 && Math.abs(markerPos.lng() - lng) < 0.00001) {
                google.maps.event.trigger(marker, 'click');
            }
        });
    }
}

/**
 * Calculate distance between two points (Haversine formula)
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
}

/**
 * Geocode location address (for edit modal)
 */
export async function geocodeLocationAddress(locationId) {
    // Try the unified modal field IDs first (locations-modals.js)
    let addressField = document.getElementById('location-address');
    let latField = document.getElementById('location-latitude');
    let longField = document.getElementById('location-longitude');
    
    // Fall back to edit modal field IDs (location-edit.js)
    if (!addressField) {
        addressField = document.getElementById('edit-location-address');
        latField = document.getElementById('edit-location-latitude');
        longField = document.getElementById('edit-location-longitude');
    }
    
    if (!addressField) {
        showError('Address field not found');
        return;
    }
    
    const address = addressField.value.trim();
    
    if (!address) {
        showError('Please enter an address first');
        return;
    }
    
    await geocodeAddress(address, latField, longField, event.target);
}

/**
 * Geocode location address (for create modal)
 */
export async function geocodeCreateLocationAddress() {
    // Try the unified modal field IDs first (locations-modals.js)
    let addressField = document.getElementById('location-address');
    let latField = document.getElementById('location-latitude');
    let longField = document.getElementById('location-longitude');
    
    // Fall back to create modal field IDs (if they exist)
    if (!addressField) {
        addressField = document.getElementById('create-location-address');
        latField = document.getElementById('create-location-latitude');
        longField = document.getElementById('create-location-longitude');
    }
    
    if (!addressField) {
        showError('Address field not found');
        return;
    }
    
    const address = addressField.value.trim();
    
    if (!address) {
        showError('Please enter an address first');
        return;
    }
    
    await geocodeAddress(address, latField, longField, event.target);
}

/**
 * Geocode an address using Google Geocoding API
 */
async function geocodeAddress(address, latField, longField, buttonElement) {
    if (!googleGeocoder) {
        googleGeocoder = new google.maps.Geocoder();
    }
    
    try {
        const originalButtonText = buttonElement.innerHTML;
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
            <svg class="animate-spin h-4 w-4 mr-1.5 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Geocoding...
        `;
        
        googleGeocoder.geocode({ address: address }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
                const result = results[0];
                const location = result.geometry.location;
                
                latField.value = location.lat().toFixed(6);
                longField.value = location.lng().toFixed(6);
                
                showSuccess(`Coordinates found: ${result.formatted_address}`);
                
            } else if (status === 'ZERO_RESULTS') {
                showError('Address not found. Please check the address and try again.');
            } else {
                throw new Error(`Geocoding failed: ${status}`);
            }
            
            buttonElement.disabled = false;
            buttonElement.innerHTML = originalButtonText;
        });
        
    } catch (error) {
        console.error('Geocoding error:', error);
        showError('Failed to geocode address. Please try again.');
        buttonElement.disabled = false;
        buttonElement.innerHTML = originalButtonText;
    }
}

// Export functions to window for onclick handlers
window.focusLocation = focusLocation;
window.geocodeLocationAddress = geocodeLocationAddress;
window.geocodeCreateLocationAddress = geocodeCreateLocationAddress;

// ===========================================================================
// Tracker (GPS) sensor view
// ===========================================================================

const trackerSensorState = {
    currentSensor: null,
    currentPeriod: 'weekly',
    selectedDate: new Date(),
    points: [],
    map: null,
    polyline: null,
    startMarker: null,
    endMarker: null
};

/**
 * Compute [start, end) datetimes based on current period and selectedDate.
 */
function computeTrackerWindow() {
    const date = new Date(trackerSensorState.selectedDate);
    let start, end;
    if (trackerSensorState.currentPeriod === 'daily') {
        start = new Date(date); start.setHours(0, 0, 0, 0);
        end = new Date(start); end.setDate(end.getDate() + 1);
    } else if (trackerSensorState.currentPeriod === '3day') {
        end = new Date(date); end.setHours(0, 0, 0, 0); end.setDate(end.getDate() + 1);
        start = new Date(end); start.setDate(start.getDate() - 3);
    } else if (trackerSensorState.currentPeriod === 'weekly') {
        const day = date.getDay();
        const daysFromMonday = day === 0 ? 6 : day - 1;
        start = new Date(date); start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - daysFromMonday);
        end = new Date(start); end.setDate(end.getDate() + 7);
    } else { // monthly
        start = new Date(date.getFullYear(), date.getMonth(), 1);
        end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }
    return { start, end };
}

function formatTrackerDateRange() {
    const { start, end } = computeTrackerWindow();
    const lastDay = new Date(end); lastDay.setDate(lastDay.getDate() - 1);
    if (trackerSensorState.currentPeriod === 'daily') {
        return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (trackerSensorState.currentPeriod === 'monthly') {
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/**
 * Haversine distance between two lat/lng points (km).
 */
function haversineKm(a, b) {
    const R = 6371;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(s));
}

async function displayTrackerSensorData(sensor) {
    const display = document.getElementById('sensor-data-display');
    if (!display) return;

    trackerSensorState.currentSensor = sensor;

    const dateInputValue = (() => {
        const d = trackerSensorState.selectedDate;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    })();

    const periodBtn = (key, label) => `
        <button onclick="window.changeTrackerPeriod('${key}')"
                class="px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    trackerSensorState.currentPeriod === key
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }">
            ${label}
        </button>
    `;

    display.innerHTML = `
        <div>
            <h4 class="text-md font-medium text-gray-900 mb-4">
                ${t('assets.trackerSensor') || 'Tracker'}: ${sensor.name || sensor.sensor_id}
            </h4>

            <div class="mb-6">
                <div class="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div class="flex space-x-2">
                        ${periodBtn('daily', t('assets.24h') || '24h')}
                        ${periodBtn('3day', t('assets.threeDays') || '3 days')}
                        ${periodBtn('weekly', t('assets.weekly') || 'Weekly')}
                        ${periodBtn('monthly', t('assets.monthly') || 'Monthly')}
                    </div>
                    <div class="flex items-center space-x-2">
                        <button onclick="window.navigateTrackerPeriod(-1)"
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div class="relative">
                            <button type="button" id="tracker-date-picker-button"
                                    class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors min-w-[200px] justify-between">
                                <span id="tracker-date-display">${formatTrackerDateRange()}</span>
                                <svg class="ml-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                                </svg>
                            </button>
                            <input type="hidden" id="tracker-date-picker-input" value="${dateInputValue}">
                            <div id="tracker-calendar-popup" class="hidden absolute left-1/2 transform -translate-x-1/2 z-50 mt-2 bg-white rounded-lg shadow-lg border border-gray-200"></div>
                        </div>
                        <button onclick="window.navigateTrackerPeriod(1)"
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        <button onclick="window.goToTodayTracker()"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                            Today
                        </button>
                        <button onclick="window.refreshTrackerData()"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50 transition-colors">
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div id="tracker-map" class="bg-white rounded-lg border border-gray-200" style="height: 500px;"></div>

            <div id="tracker-stats" class="grid grid-cols-4 gap-4 mt-6"></div>
        </div>
    `;

    setTimeout(() => initializeTrackerCalendarPicker(), 100);
    await loadTrackerData();
}

async function loadTrackerData() {
    const sensor = trackerSensorState.currentSensor;
    if (!sensor) return;

    const mapEl = document.getElementById('tracker-map');
    const statsEl = document.getElementById('tracker-stats');
    if (!mapEl) return;

    mapEl.innerHTML = `
        <div class="text-center py-16">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-500">Loading GPS trail...</p>
        </div>
    `;

    const { start, end } = computeTrackerWindow();
    const url = `${API_BASE_URL}/sensors/${sensor._id || sensor.id}/gps-logs`
        + `?start_date=${encodeURIComponent(start.toISOString())}`
        + `&end_date=${encodeURIComponent(end.toISOString())}`;

    try {
        const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${auth.getToken()}` } });
        if (!resp.ok) throw new Error('Failed to load GPS logs');
        const data = await resp.json();
        trackerSensorState.points = data.points || [];
        renderTrackerMap();
        renderTrackerStats();
    } catch (err) {
        console.error('Error loading tracker data:', err);
        mapEl.innerHTML = `
            <div class="text-center py-16 text-gray-500">
                <p class="text-sm">Failed to load GPS trail</p>
            </div>
        `;
        if (statsEl) statsEl.innerHTML = '';
    }
}

function renderTrackerMap() {
    const mapEl = document.getElementById('tracker-map');
    if (!mapEl) return;

    const points = trackerSensorState.points;
    if (!points || points.length === 0) {
        mapEl.innerHTML = `
            <div class="text-center py-16 text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p class="mt-4 text-sm">No GPS points recorded in this period</p>
            </div>
        `;
        return;
    }

    if (typeof google === 'undefined' || !google.maps) {
        mapEl.innerHTML = `<div class="text-center py-16 text-red-600 text-sm">Google Maps not loaded</div>`;
        return;
    }

    // Reset container to a plain div for Google Maps to mount into
    mapEl.innerHTML = '';

    const path = points.map(p => ({ lat: p.lat, lng: p.lon }));
    const bounds = new google.maps.LatLngBounds();
    path.forEach(pt => bounds.extend(pt));

    const map = new google.maps.Map(mapEl, {
        center: path[Math.floor(path.length / 2)],
        zoom: 13,
        mapTypeControl: true,
        streetViewControl: false
    });
    trackerSensorState.map = map;

    trackerSensorState.polyline = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 0.9,
        strokeWeight: 4,
        map: map
    });

    const first = points[0];
    const last = points[points.length - 1];

    trackerSensorState.startMarker = new google.maps.Marker({
        position: { lat: first.lat, lng: first.lon },
        map: map,
        title: `Start: ${new Date(first.ts).toLocaleString()}`,
        icon: { url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' }
    });

    trackerSensorState.endMarker = new google.maps.Marker({
        position: { lat: last.lat, lng: last.lon },
        map: map,
        title: `End: ${new Date(last.ts).toLocaleString()}`,
        icon: { url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' }
    });

    if (path.length > 1) {
        map.fitBounds(bounds);
    }
}

function renderTrackerStats() {
    const statsEl = document.getElementById('tracker-stats');
    if (!statsEl) return;

    const points = trackerSensorState.points;
    if (!points || points.length === 0) {
        statsEl.innerHTML = '';
        return;
    }

    let distanceKm = 0;
    let maxKnots = 0;
    let sumKnots = 0;
    let speedCount = 0;
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (typeof p.sog === 'number') {
            sumKnots += p.sog;
            speedCount += 1;
            if (p.sog > maxKnots) maxKnots = p.sog;
        }
        if (i > 0) {
            distanceKm += haversineKm(points[i - 1], p);
        }
    }
    const avgKnots = speedCount > 0 ? sumKnots / speedCount : 0;

    const card = (color, title, value, sub) => `
        <div class="bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg p-5 border border-${color}-200">
            <p class="text-xs font-medium text-${color}-600 uppercase tracking-wide">${title}</p>
            <p class="mt-2 text-3xl font-bold text-${color}-900">${value}</p>
            ${sub ? `<p class="text-xs text-${color}-700 mt-1">${sub}</p>` : ''}
        </div>
    `;

    statsEl.innerHTML = [
        card('blue', 'Points', points.length, formatTrackerDateRange()),
        card('green', 'Distance', `${distanceKm.toFixed(2)} km`, 'Sum of segment distances'),
        card('amber', 'Avg speed', `${avgKnots.toFixed(1)} kn`, speedCount === 0 ? 'no speed data' : `${speedCount} samples`),
        card('rose', 'Max speed', `${maxKnots.toFixed(1)} kn`, '')
    ].join('');
}

function initializeTrackerCalendarPicker() {
    const dateInput = document.getElementById('tracker-date-picker-input');
    const button = document.getElementById('tracker-date-picker-button');
    if (!dateInput || !button) return;

    createCalendarPicker({
        inputId: 'tracker-date-picker-input',
        buttonId: 'tracker-date-picker-button',
        displayId: 'tracker-date-display',
        popupId: 'tracker-calendar-popup',
        isRange: false,
        defaultDate: trackerSensorState.selectedDate,
        onChange: async (dates) => {
            if (dates && dates.startDate) {
                trackerSensorState.selectedDate = dates.startDate;
                const disp = document.getElementById('tracker-date-display');
                if (disp) disp.textContent = formatTrackerDateRange();
                await loadTrackerData();
            }
        }
    });
}

window.changeTrackerPeriod = async function(period) {
    trackerSensorState.currentPeriod = period;
    // Re-render the surrounding controls (so active button updates), then reload
    if (trackerSensorState.currentSensor) {
        await displayTrackerSensorData(trackerSensorState.currentSensor);
    }
};

window.navigateTrackerPeriod = async function(direction) {
    const date = new Date(trackerSensorState.selectedDate);
    const period = trackerSensorState.currentPeriod;
    if (period === 'daily') date.setDate(date.getDate() + direction);
    else if (period === '3day') date.setDate(date.getDate() + direction * 3);
    else if (period === 'weekly') date.setDate(date.getDate() + direction * 7);
    else date.setMonth(date.getMonth() + direction);
    trackerSensorState.selectedDate = date;
    const disp = document.getElementById('tracker-date-display');
    if (disp) disp.textContent = formatTrackerDateRange();
    await loadTrackerData();
};

window.goToTodayTracker = async function() {
    trackerSensorState.selectedDate = new Date();
    const disp = document.getElementById('tracker-date-display');
    if (disp) disp.textContent = formatTrackerDateRange();
    await loadTrackerData();
};

window.refreshTrackerData = async function() {
    await loadTrackerData();
    showSuccess('GPS trail refreshed');
};
